/* ============================================================
   LEARN COMPONENTS — the shared component library
   File: assets/learn-components.js
   Loaded on every Learn tool page via sections/learn-subnav.liquid,
   the same entry point as learn-hub-analytics.js.

   v1 ships THE CAKE CHIP (§3.1) — read-only. It DISPLAYS the shared
   cake (localStorage `layerd-learn-tiers`), its origin, and whether
   the current tool is diverging to its own cake. It NEVER writes the
   shared state — every tool keeps ownership of its own writes, so
   this component cannot corrupt the contract or orphan saved data.

   A tool opts in by dropping a placeholder anywhere on the page:

     <div data-ld-cakechip data-ld-tool="structure"
          data-ld-target="#lstr-controls"></div>

   Optional attributes:
     data-ld-detached="true"          this tool is on "a different cake"
     data-ld-color="var(--cake-2)"    wear a Planner booking's colour
     data-ld-empty-url="/pages/..."   where to send a user with no cake yet
     data-ld-summary="10″ + 8″ · round"   TOOL-DRIVEN content: when a tool
     data-ld-origin="from Serving"        wants the chip to mirror its OWN
                                          state (so chip and tool can never
                                          disagree), it supplies the summary
                                          + origin and the chip skips the
                                          localStorage read. Without these,
                                          the chip auto-reads the shared cake
                                          (the lightweight consumer path).

   The JS only touches elements that carry the placeholder attribute,
   so it is inert on tools that haven't adopted it. Fails silently if
   storage is unavailable (private mode).

   Tools that WRITE the shared cake should, right after writing,
   dispatch  window.dispatchEvent(new Event('layerd:cake-updated'))
   so every chip on the page refreshes (and update their placeholder's
   data-ld-detached to match a "different cake" toggle).
   ============================================================ */
(function () {
  'use strict';

  var KEY = 'layerd-learn-tiers';
  var ORIGINS = {
    serving: 'Serving', structure: 'Structure', 'batch-math': 'Batch Math',
    pricing: 'Pricing', planner: 'the Planner', timeline: 'the Planner'
  };

  /* newest stamp across localStorage (cross-tab) + sessionStorage
     (legacy) — the exact contract every tool's reader uses. */
  function readShared() {
    var best = null;
    [localStorage, sessionStorage].forEach(function (store) {
      try {
        var v = JSON.parse(store.getItem(KEY));
        if (v && v.tiers && v.tiers.length && (!best || (v.at || 1) > (best.at || 1))) best = v;
      } catch (e) { /* private mode / corrupt — treat as no cake */ }
    });
    return best;
  }

  function el(tag, attrs, kids) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === 'text') n.textContent = attrs[k];
      else if (k === 'class') n.className = attrs[k];
      else if (k.slice(0, 2) === 'on') n.addEventListener(k.slice(2), attrs[k]);
      else n.setAttribute(k, attrs[k]);
    });
    (kids || []).forEach(function (c) { if (c) n.appendChild(c); });
    return n;
  }

  /* tiers are stored in inches (the contract's canonical unit); the label
     follows the shared units setting (r33 — see chipSize below) */
  function fmtSize(d) {
    try { if (localStorage.getItem('layerd-learn-units') === 'cm') return Math.round((+d) * 2.54) + ' cm'; } catch (e) { }
    return (Math.round((+d) * 10) / 10) + '″';
  }

  function summarize(cake) {
    var tiers = cake.tiers.slice().sort(function (a, b) { return (b.d || 0) - (a.d || 0); });
    var sizes = tiers.map(function (t) { return fmtSize(t.d); }).join(' + ');
    var shapes = {};
    tiers.forEach(function (t) { shapes[t.shape === 'square' ? 'square' : 'round'] = 1; });
    var keys = Object.keys(shapes);
    return sizes + ' · ' + (keys.length === 1 ? keys[0] : 'mixed shapes');
  }
  function originLabel(from) { return ORIGINS[from] || (from || 'the Learn tools'); }

  /* ---- every tool opens at the TOP (Mariya, 2026-07-31) ----
     "the top tool navigation bar, when you click one of them it always
     loads the page near the middle." That is the BROWSER restoring where
     you were the last time you were on that page — nothing in the tab
     links or our code moves the page. Turning restoration off makes each
     tool open where it starts. A real deep link (#quote, #arcpCombos,
     ?booking=…#…) still lands where it points, because the browser's
     fragment jump is separate from restoration.
     The Planner already did this for itself (learn-timeline-hub.js:743);
     it belongs to the shared layer so every tool behaves the same. */
  try {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  } catch (eSR) { }

  /* ============ THE one scroll utility (nav rule, 2026-07-20) ============
     Every programmatic viewport move in the Toolkit goes through here, and
     it is only ever called from a user navigation click (or the cross-page
     landing that completes one). Nothing else may call a scroll API.
       navigateToSection(target, { block, focus, stickySel })
       holdScroll(fn) — run a re-render and keep the viewport where it was */
  function navigateToSection(target, opts) {
    opts = opts || {};
    var el2 = typeof target === 'string'
      ? (document.getElementById(target) || document.querySelector(target))
      : target;
    if (!el2) return false;
    var det = el2.tagName === 'DETAILS' ? el2 : (el2.closest ? el2.closest('details') : null);
    if (det) det.open = true;
    var rm = false;
    try { rm = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { }
    var off = 10;
    if (opts.stickySel) {
      var sb = document.querySelector(opts.stickySel);
      if (sb) off += sb.getBoundingClientRect().height;
    }
    function jump(instant) {
      var r0 = el2.getBoundingClientRect();
      var top = r0.top + (window.pageYOffset || document.documentElement.scrollTop || 0) - off;
      if (opts.block === 'center') top -= Math.max(0, (window.innerHeight - r0.height) / 2 - off);
      try { window.scrollTo((instant || rm) ? { top: top } : { top: top, behavior: 'smooth' }); }
      catch (e) { window.scrollTo(0, top); }
    }
    /* opts.instant: for a RETURN rather than a navigation (coming back to
       the file bar after a save). Smooth is for "take me somewhere"; snapping
       back to where you already were should not animate — and a short hop
       can fall inside the arrival guarantee's tolerance and never land. */
    jump(!!opts.instant);
    setTimeout(function () {
      /* arrival guarantee: smooth can be silently unsupported or cancelled
         by layout shifts — if we haven't arrived, jump instantly */
      var r = el2.getBoundingClientRect();
      if (r.top < -40 || r.top > window.innerHeight * 0.7) jump(true);
      if (opts.focus) {
        var f2 = opts.focus === true
          ? ((el2.matches && el2.matches('input,select,textarea,button,[tabindex]')) ? el2 : el2.querySelector('input, select, textarea, button, [tabindex]'))
          : (typeof opts.focus === 'string' ? el2.querySelector(opts.focus) : opts.focus);
        if (f2) { try { f2.focus({ preventScroll: true }); } catch (e2) { try { f2.focus(); } catch (e3) { } } }
      }
    }, 700);
    return true;
  }
  function holdScroll(fn) {
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    fn();
    window.scrollTo(0, y);
  }
  function jumpTo(sel) {
    if (!sel) return;
    navigateToSection(sel, { focus: true });
  }

  /* ---- Client File plumbing (Client File & Quote Engine §4.5) ---- */
  var BOOKINGS_KEY = 'layerd-academy-bookings';
  /* client-file identity ramp — mirrors --cake-1..4 in learn-tokens.css.
     Slot 0 stays the AUBERGINE and does not follow the brand colour: at
     7px a near-black dot is indistinguishable from the ink chrome. */
  var CAKE_HEX = ['#4C3A50', '#6F7D62', '#BCA069', '#7A4A55'];
  var STATUS_LABEL = { inquiry: 'Inquiry', quoted: 'Quoted', booked: 'Booked', archived: 'Archived' };
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var DOWS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  function readBookings() {
    /* Toolkit Fix 01: the store is v2 (booking-scoped cake files); the
       normalizer in learn-cakefile.js is the source of truth when loaded */
    if (window.LdCakeFile && window.LdCakeFile.readStore) return window.LdCakeFile.readStore().bookings;
    try {
      var v = JSON.parse(localStorage.getItem(BOOKINGS_KEY));
      if (v && (v.v === 1 || v.v === 2) && Object.prototype.toString.call(v.bookings) === '[object Array]') return v.bookings;
    } catch (e) { /* private mode / absent */ }
    return [];
  }
  function bookingById(id) {
    var bs = readBookings();
    for (var i = 0; i < bs.length; i++) if (bs[i].id === id) return bs[i];
    return null;
  }
  function statusOf(b) { return (b && b.status) || 'booked'; }
  function niceDate(iso) {
    if (!iso) return '';
    var p = String(iso).split('-');
    if (p.length !== 3) return '';
    var d = new Date(+p[0], +p[1] - 1, +p[2]);
    if (isNaN(d.getTime())) return '';
    return DOWS[d.getDay()] + ' ' + MONTHS[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
  }
  function cakeHex(ci) { return CAKE_HEX[(((ci == null ? 0 : ci) % 4) + 4) % 4]; }

  /* r33 (Mariya, 2026-08-01): "the top client bar doesn't change the
     sizing to centimeters" — the bar hardcoded ″ while every calculator
     obeys the shared units key. One formatter, read at render time, so
     the bar follows the Toolkit setting AND any page's inline flip.
     Tiers are STORED in inches (the contract's canonical unit) — this
     only changes what is said, never what is saved. */
  function chipUnits() {
    try { return localStorage.getItem('layerd-learn-units') === 'cm' ? 'cm' : 'in'; }
    catch (e) { return 'in'; }
  }
  function chipSize(d) {
    if (chipUnits() === 'cm') return Math.round((+d) * 2.54) + ' cm';
    return (Math.round((+d) * 10) / 10) + '″';
  }

  /* file tiers → one honest line, HEIGHT included (Toolkit Fix 02: heights
     were invisible, so a flattened cake looked fine). "10″ + 8″ · round",
     plus " · 6″ tall" when uniform-tall or " · mixed heights". */
  function summarizeFileTiers(tiers) {
    if (!tiers || !tiers.length) return '';
    var ts = tiers.slice().sort(function (a, b) { return (b.d || 0) - (a.d || 0); });
    var sizes = ts.map(function (t) { return chipSize(+t.d || 6); }).join(' + ');
    var shapes = {}, hs = {};
    ts.forEach(function (t) {
      shapes[t.shape === 'square' ? 'square' : t.shape === 'heart' ? 'heart' : 'round'] = 1;
      hs[+t.h || 4] = 1;
    });
    var sk = Object.keys(shapes), hk = Object.keys(hs);
    var out = sizes + ' · ' + (sk.length === 1 ? sk[0] : 'mixed shapes');
    if (hk.length > 1) out += ' · mixed heights';
    else if (+hk[0] !== 4) out += ' · ' + chipSize(+hk[0]) + ' tall';
    return out;
  }

  function build(mount) {
    var detached = mount.getAttribute('data-ld-detached') === 'true';
    var target = mount.getAttribute('data-ld-target') || '';
    var color = mount.getAttribute('data-ld-color') || 'var(--cake-1, #2A2138)';
    var emptyUrl = mount.getAttribute('data-ld-empty-url') || '/pages/serving-size-calculator';
    /* tool-driven content (the chip mirrors the tool's own state) vs the
       lightweight auto path (read the shared cake straight from storage) */
    var ownSummary = mount.getAttribute('data-ld-summary') || '';
    var ownOrigin = mount.getAttribute('data-ld-origin') || '';

    /* ---- ONE SOURCE OF TRUTH (Toolkit Fix 02 P0) ----
       The chip renders from the SAME readCake() the calculators use. An
       active client file wins over everything a tool pushed via attributes —
       the old attribute-first order let a scoped page show the scratch
       state, whose "attach" tap overwrote the file with stale geometry. */
    var rc = (window.LdCakeFile && window.LdCakeFile.readCake) ? window.LdCakeFile.readCake() : null;
    var scopedB = rc && rc.booking ? rc.booking : null;

    var clientName, ci, status, dateLbl, fileTierLine = '';
    if (scopedB) {
      clientName = (scopedB.name || '').trim() || 'Cake order';
      ci = (scopedB.colorIndex == null) ? 0 : scopedB.colorIndex;
      status = scopedB.status || '';
      dateLbl = scopedB.date ? niceDate(scopedB.date) : '';
      fileTierLine = summarizeFileTiers(rc.tiers);
      if (rc.source !== 'booking') fileTierLine = 'No tiers on this file yet';
    } else if (!rc) {
      /* legacy attribute path — ONLY when the cake-file layer is absent.
         With the layer present, readCake is authoritative both ways: a
         stale data-ld-client left by an earlier scoped render must not
         keep the chip in client state after unscoping. */
      clientName = mount.getAttribute('data-ld-client') || '';
      ci = parseInt(mount.getAttribute('data-ld-ci') || '0', 10) || 0;
      status = mount.getAttribute('data-ld-status') || '';
      dateLbl = mount.getAttribute('data-ld-date') || '';
    } else {
      clientName = '';
    }
    var cake = (clientName || ownSummary) ? null : readShared();

    /* dev-mode assertion (brief §P0-2): the chip's source must equal the
       calculators' source. Enable with localStorage 'ld-debug'. */
    try {
      if (rc && localStorage.getItem('ld-debug')) {
        var chipSource = scopedB ? 'booking' : (ownSummary || cake ? 'scratch' : 'empty');
        console.assert(chipSource === (rc.source === 'derived' ? 'booking' : rc.source),
          '[ld-debug] chip source "' + chipSource + '" != readCake "' + rc.source + '"');
      }
    } catch (e) { }

    /* ---- 2026-08-03 (Mariya): SAY THE SAVE OUT LOUD ----
       "the hub promises every tool saves straight back to the client file,
       but nothing on the Serving page mentions the file at all … the
       pipeline promise is broken at step one, which is the step every
       inquiry starts with."

       Serving DID say so — but only far down in the results area, where a
       re-render had already been caught destroying the note. The bar, which
       is what she actually looks at, said nothing about saving.

       This reads the SAME `PIPE_STEPS[here].done(b)` the dots read, so the
       words and the dot can never disagree and no second notion of "saved"
       enters the codebase. Returns null off-pipeline (the Planner, an
       unknown page) — there is nothing to claim there. */
    var savedState = (function () {
      if (!scopedB) return null;
      /* WHICH step this page is. The mount declares its own tool
         (data-ld-tool, set in every tool's template) — that is an explicit
         fact, so it is trusted first. pipeUrls().here only sniffs the
         subnav for an is-active class, which is absent in every preview rig
         and would be absent live the moment a theme stops marking the
         active tab — and a silently missing line is exactly the broken
         promise this was built to fix. */
      var key = TOOL_STEP[(mount.getAttribute('data-ld-tool') || '').toLowerCase()] || null;
      if (!key) { try { key = pipeUrls().here; } catch (e) { return null; } }
      if (!key) return null;
      for (var i = 0; i < PIPE_STEPS.length; i++) {
        if (PIPE_STEPS[i].key !== key) continue;
        try { return PIPE_STEPS[i].done(scopedB) ? 'saved' : 'unsaved'; }
        catch (e) { return null; }
      }
      return null;
    })();

    var prevSig = mount.getAttribute('data-ld-sig') || '';
    /* savedState rides the sig: rc.meta.updatedAt moves when THIS tool
       writes, but another tool completing this step would otherwise leave
       the line stale */
    var sig = (clientName ? 'client:' + clientName + '|' + status + '|' + (fileTierLine || ownSummary) + '|' + (rc && rc.meta ? rc.meta.updatedAt : '') + '|' + savedState
      : (ownSummary ? ownSummary + '|' + ownOrigin : (cake ? cake.at : 'none'))) + '|' + detached;

    var node;
    if (clientName) {
      /* ---- THE CLIENT-FILE BAR (design addendum §2, 2026-07-30) ----
         ONE bar, identical on every tool page. Mariya: "some have the bar,
         some have the drop down, the dots are not clear what they do
         without a next button." So: colour spine, the file's name and date,
         the cake it is, the six pipeline dots, `change`, and the ONE next
         outstanding step as a pill. The dots and the pill both read
         PIPE_STEPS + stepHref, so they can never disagree with each other
         or with the file page. */
      /* 2026-08-11 (Mariya: "can we actually start the date on a new
         line?") \u2014 the name and date were one joined string, so a longer
         name pushed the date into an ugly mid-date wrap (her Emma Walsh
         Collins screenshot). Two elements now; the body is a flex column,
         so the date always starts its own line instead of wrapping
         wherever the name happens to end. */
      var cBody = [el('span', { class: 'ld-cakechip__name', text: clientName })];
      if (dateLbl) cBody.push(el('span', { class: 'ld-cakechip__when', text: dateLbl }));
      /* the tier line comes from the FILE when scoped (heights included) —
         a tool-pushed summary is only a fallback for legacy pages */
      var tierLine = fileTierLine || ownSummary;
      if (tierLine) cBody.push(el('span', { class: 'ld-cakechip__spec', text: tierLine }));
      /* the promise, kept in words — the dots say it in circles */
      if (savedState) {
        var poss = clientName + (/s$/i.test(clientName) ? '’' : '’s');
        cBody.push(el('span', {
          class: 'ld-cakechip__saved' + (savedState === 'saved' ? ' is-saved' : ''),
          text: savedState === 'saved'
            ? 'Saved to ' + poss + ' file ✓'
            : 'Not saved to ' + poss + ' file yet'
        }));
      }
      var dotsRow = scopedB ? pipeDots(scopedB) : null;
      if (dotsRow) cBody.push(dotsRow);

      var cChange = el('button', {
        class: 'ld-cakechip__change', type: 'button', text: 'change',
        'aria-label': 'Change which client file this tool is working on',
        'aria-expanded': 'false', 'aria-haspopup': 'true'
      });
      var cAside = [cChange];
      var np = scopedB ? nextPill(scopedB) : null;
      if (np) cAside.push(np);

      node = el('div', { class: 'ld-cakechip ld-cakechip--client' }, [
        el('span', { class: 'ld-cakechip__body' }, cBody),
        el('span', { class: 'ld-cakechip__aside' }, cAside)
      ]);
      node.style.setProperty('--c', cakeHex(ci));
      wirePopover(node, cChange, scopedB ? scopedB.id : '');
    } else if (window.LdCakeFile && window.LdCakeFile.displayGated &&
               window.LdCakeFile.displayGated() && readBookings().length) {
      /* r57 (Mariya): the display gate. This device holds account-synced
         client files and the browser is logged out — the bar says exactly
         that instead of faking emptiness ("no client file" would be a lie,
         and an "attach" popover would list the very names the gate hides).
         The tools underneath keep working unscoped; nothing is deleted. */
      var gBody = [
        el('span', { class: 'ld-cakechip__name ld-cakechip__name--none', text: 'Logged out. Client files hidden' }),
        el('span', { class: 'ld-cakechip__spec', text: 'Log in and they come right back. Nothing has been deleted.' })
      ];
      /* r58: the NEW-customer-accounts login (see learn-subnav.liquid,
         2026-07-29) — return_to is what brings her straight back to this
         tool page instead of stranding her on the hosted order history. */
      var gLogin = el('a', {
        class: 'ld-cakechip__change ld-cakechip__login',
        href: '/customer_authentication/login?return_to=' + encodeURIComponent(location.pathname + location.search),
        text: 'log in'
      });
      node = el('div', { class: 'ld-cakechip ld-cakechip--client ld-cakechip--none ld-cakechip--gated' }, [
        el('span', { class: 'ld-cakechip__body' }, gBody),
        el('span', { class: 'ld-cakechip__aside' }, [gLogin])
      ]);
    } else if (unattachedMember()) {
      /* files exist, none scoped: the design's unattached bar. Same shape,
         so switching a file on or off never reflows the page. */
      var uBody = [el('span', { class: 'ld-cakechip__name ld-cakechip__name--none', text: 'Just the math \u2014 no client file' })];
      var uSpec = ownSummary || (cake ? summarize(cake) : '');
      if (uSpec) uBody.push(el('span', { class: 'ld-cakechip__spec', text: uSpec }));
      var uAttach = el('button', {
        class: 'ld-cakechip__change', type: 'button', text: 'attach a client',
        'aria-label': 'Attach a client file to this tool',
        'aria-expanded': 'false', 'aria-haspopup': 'true'
      });
      node = el('div', { class: 'ld-cakechip ld-cakechip--client ld-cakechip--none' }, [
        el('span', { class: 'ld-cakechip__body' }, uBody),
        el('span', { class: 'ld-cakechip__aside' }, [uAttach])
      ]);
      wirePopover(node, uAttach, '');
    } else if ((function syncStillChecking() {
      /* r85 (Mariya's wipe scare): a freshly reinstalled app shows ZERO
         files for the few seconds before the boot pull lands — and an
         empty client list at that moment reads as data loss, which is
         exactly the panic she just lived. While sync is possible and no
         pull has completed, the bar says the true thing; the pull's own
         merge event re-renders this bar the moment files arrive. */
      try {
        if (activeFiles('').length) return false;
        var sy = window.LAYERD && window.LAYERD.cloudSync && window.LAYERD.cloudSync.status();
        return !!(sy && sy.enabled !== false && !sy.lastPull);
      } catch (e) { return false; }
    })()) {
      node = el('div', { class: 'ld-cakechip ld-cakechip--client ld-cakechip--none ld-cakechip--checking' }, [
        el('span', { class: 'ld-cakechip__body' }, [
          el('span', { class: 'ld-cakechip__name ld-cakechip__name--none', text: 'Checking your account for client files…' }),
          el('span', { class: 'ld-cakechip__spec', text: 'Nothing is missing. This device is syncing.' })
        ])
      ]);
    } else if (startFileOffer()) {
      /* ---- 2026-08-03 (Mariya): THE FIRST-TIMER'S DOOR ----
         "if the pipeline starts at serving, is there an option to start a
         client file after choosing a size? Perhaps this is a good way to
         funnel someone into opening a client file and trying the full suite?"

         Serving already builds exactly this (startFileHtml/wireStartFile) —
         but it renders at the END of the results, the same buried spot as
         the save note she never saw. And the bar's "attach a client" branch
         above requires files to ALREADY EXIST (unattachedMember), so the one
         person we want to funnel — nobody's client yet, no files — got no
         door at all in the place they are looking.

         Shown only once a cake has been sized: it adopts THAT cake, so the
         offer is never abstract. The in-results callout stays; it explains
         the value properly. This is the visible second door. */
      var sBody = [el('span', { class: 'ld-cakechip__name ld-cakechip__name--none', text: 'Just the math, no client file' })];
      var sSpec = ownSummary || (cake ? summarize(cake) : '');
      if (sSpec) sBody.push(el('span', { class: 'ld-cakechip__spec', text: sSpec }));
      var sGo = el('button', {
        class: 'ld-cakechip__change ld-cakechip__start', type: 'button',
        text: 'start a client file',
        'aria-label': 'Start a client file from this cake'
      });
      node = el('div', { class: 'ld-cakechip ld-cakechip--client ld-cakechip--none ld-cakechip--start' }, [
        el('span', { class: 'ld-cakechip__body' }, sBody),
        el('span', { class: 'ld-cakechip__aside' }, [sGo])
      ]);
      wireStartFileChip(node, sGo);
    } else if (ownSummary) {
      var oDot = el('span', { class: 'ld-cakechip__dot' + (detached ? ' ld-cakechip__dot--hollow' : ''), 'aria-hidden': 'true' });
      oDot.style.setProperty('--c', color);
      var oChange = el('button', {
        class: 'ld-cakechip__change', type: 'button', text: detached ? 'switch' : 'change',
        'aria-label': (detached ? 'Switch which cake this tool uses' : 'Change the cake') + ', jump to the controls'
      });
      oChange.addEventListener('click', function () { jumpTo(target); });
      node = el('div', { class: 'ld-cakechip' + (detached ? ' is-detached' : '') }, [
        oDot,
        el('span', { class: 'ld-cakechip__body' }, [
          el('span', { class: 'ld-cakechip__summary', text: ownSummary }),
          el('span', { class: 'ld-cakechip__origin', text: ownOrigin || (detached ? 'this tool’s own cake' : 'your cake') })
        ]),
        oChange
      ]);
    } else if (!cake) {
      /* an honest empty state — never silent about which cake is in play */
      node = el('div', { class: 'ld-cakechip ld-cakechip--empty' }, [
        el('span', { class: 'ld-cakechip__dot ld-cakechip__dot--hollow', 'aria-hidden': 'true' }),
        el('span', { class: 'ld-cakechip__body' }, [
          el('span', { class: 'ld-cakechip__summary', text: 'No cake saved yet' }),
          el('span', { class: 'ld-cakechip__origin', text: 'using this tool’s own tiers' })
        ]),
        el('a', { class: 'ld-cakechip__change', href: emptyUrl, text: 'size one →' })
      ]);
    } else {
      var dot = el('span', { class: 'ld-cakechip__dot' + (detached ? ' ld-cakechip__dot--hollow' : ''), 'aria-hidden': 'true' });
      dot.style.setProperty('--c', color);
      var change = el('button', {
        class: 'ld-cakechip__change', type: 'button', text: detached ? 'switch' : 'change',
        'aria-label': (detached ? 'Switch which cake this tool uses' : 'Change the shared cake') + ', jump to the controls'
      });
      change.addEventListener('click', function () { jumpTo(target); });
      node = el('div', { class: 'ld-cakechip' + (detached ? ' is-detached' : '') }, [
        dot,
        el('span', { class: 'ld-cakechip__body' }, [
          el('span', { class: 'ld-cakechip__summary', text: summarize(cake) }),
          el('span', { class: 'ld-cakechip__origin', text: detached ? 'this tool’s own cake' : 'from ' + originLabel(cake.from) })
        ]),
        change
      ]);
    }

    /* The transient "Saved to X ✓" flash is RETIRED (Mariya, 2026-07-30:
       update instantly, in place). The bar now answers "did that save?"
       structurally — the dot fills and the Next pill rolls forward — which
       survives longer than six seconds. The old unattached "choose a
       client →" warn line is retired with it: the unattached bar above
       carries "attach a client" in the same place on every page. */

    while (mount.firstChild) mount.removeChild(mount.firstChild);
    mount.appendChild(node);
    mount.setAttribute('data-ld-ready', '1');

    /* subtle pulse only when the cake actually changed under the user */
    if (prevSig && prevSig !== sig) {
      node.classList.add('is-updated');
      setTimeout(function () { node.classList.remove('is-updated'); }, 900);
    }
    mount.setAttribute('data-ld-sig', sig);
  }

  function renderAll() {
    var mounts = document.querySelectorAll('[data-ld-cakechip]');
    Array.prototype.forEach.call(mounts, build);
  }

  window.addEventListener('storage', function (e) { if (!e || e.key === KEY || e.key === null) renderAll(); });
  window.addEventListener('focus', renderAll);
  window.addEventListener('layerd:cake-updated', renderAll);
  /* r33: the bar's sizes follow the units setting, so a flip of any page's
     inline inches/cm control re-renders the bar too. Delegated — the
     toggles are page-owned and re-created on render, so we listen once at
     the document for the buttons every tool marks with data-unit (plus the
     settings panel's own segments, which dispatch cake-updated already). */
  document.addEventListener('click', function (ev) {
    var b = ev.target && ev.target.closest ? ev.target.closest('[data-unit], .ldc-units-btn, .ld-unitrow button') : null;
    if (b) setTimeout(renderAll, 0);
  });

  /* ---- the save signal (2026-07-30) ----
     Every tool already announces a save on `layerd:cakefile` with its own
     `source` (serving / structure / batch-math / pricing). One listener
     therefore covers the whole suite: re-render every bar so the dots fill
     and the Next pill rolls forward with no reload, then bring the bar
     back into view — Mariya: "when I save things, the page will auto-jump
     to the bottom. all saves should jump the page back to that topbar so
     the user can immediately see what's next."

     Deliberately NOT scrolled: `active` (the picker, which navigates
     anyway) and any event without a source (a storage echo from another
     tab, or a first paint). */
  /* 2026-08-12 (Mariya): "the flavour page will automatically jump up on the
     page after I select a flavour for ONE tier. It should only jump to the
     top to go to the next step if ALL tiers have flavours chosen."

     Right, and the general rule was hiding in the original reason for the
     scroll: the bar comes back so she can see WHAT'S NEXT. If the save did
     not finish the step, there is no new "next" to look at and she is still
     mid-task — so it should stay where she is working. A three-tier cake
     used to yank her to the top three times.

     One rule, every tool: scroll only when the save COMPLETED its own
     pipeline step. Nothing here is Recipes-specific. */
  function saveCompletedItsStep(source, b) {
    var P = window.LdCakeFile && window.LdCakeFile.predicates;
    /* no shared engine, or unscoped scratch work: the old behaviour */
    if (!P || !b) return true;
    var s = ((source || '') + '').toLowerCase();
    /* the STRICT flavour test, not the pipeline's flavoursDone — that one
       carries her "went straight to Batch Math" hatch so the DOT does not
       nag, which is a different question from whether she has finished
       assigning here. Faux tiers are excluded (they need no flavour). */
    if (/recipe|flavour/.test(s)) return P.everyTierHasFlavour(b);
    if (/serving/.test(s)) return P.sizeConfirmed(b);
    if (/structure/.test(s)) return P.structureDone(b);
    if (/batch/.test(s)) return P.batchDone(b);
    if (/pricing|quote/.test(s)) return P.quoteDone(b);
    if (/planner|timeline/.test(s)) return P.bookedDone(b);
    return true;   /* unknown source: never make it worse than before */
  }
  /* r132 (Mariya: "when I am selecting items under elements, it will keep
     jumping back up to the top client chip"). Every small write fires this
     event - an element toggle, a band change, a note edit - and
     saveCompletedItsStep() answers "yes, priced" for all of them once a
     quote exists, so the viewport was pulled to the bar on every tick.

     The move was only ever meant to mark a step being FINISHED. So it now
     fires on the TRANSITION into done, not on every save while it is
     already done. Editing a completed step no longer moves the page. */
  var ldStepWasDone = {};
  /* r132b: a step that was ALREADY done when the page opened must not look
     like a fresh completion on her first edit - that was still yanking the
     viewport on the first element she ticked. Prime the map from the file
     as it stands on arrival (and whenever the active file changes), so
     only a genuine not-done -> done transition moves anything. */
  var LD_STEP_SOURCES = ['serving', 'structure', 'batch', 'pricing', 'quote', 'planner', 'timeline'];
  function ldPrimeStepState() {
    var b = null;
    try { b = window.LdCakeFile && window.LdCakeFile.getActiveBooking(); } catch (e) { b = null; }
    LD_STEP_SOURCES.forEach(function (src) {
      var key = src + ':' + ((b && b.id) || '-');
      if (ldStepWasDone[key] === undefined) {
        try { ldStepWasDone[key] = saveCompletedItsStep(src, b); } catch (e2) { ldStepWasDone[key] = true; }
      }
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ldPrimeStepState);
  } else { ldPrimeStepState(); }

  /* 2026-09-11 (Mariya): the shared Studio-style bottom tab bar for tool pages.
     One source here, so it appears on every tool and reads as one app; the old
     top sub-nav is hidden via CSS. Skipped on the Studio itself (it has its own
     ldsp-tabs) and anywhere without a .learn-page. */
  function mountToolTabbar() {
    if (document.querySelector('.lh-tabbar') || document.querySelector('.ldsp-tabs')) return;
    /* 2026-09-18 (Mariya): an Academy-only tool (Bloom Atlas) loads this bundle
       for its shared helpers but must NOT wear the Toolkit's tab bar / desktop
       sidebar - it lives in the Academy, not the Toolkit. The .ld-academy-tool
       marker on its root opts it out (see also the return-to-studio rewrite). */
    if (document.querySelector('.ld-academy-tool')) return;
    /* 2026-09-11 FIX: the Studio (learn-toolkit-spine) ALSO has a .learn-page and
       renders its OWN .ldsp-tabs a beat later via JS, so the .ldsp-tabs guard
       above misses it at DOMContentLoaded and this bar double-mounted on the
       Studio, on top of its real tabs (broken clicks, stuck highlight).
       2026-09-11 (round 2): the Studio's mount is <div id="ld-toolkit"> in the
       STATIC html; the spine only adds the .ld-toolkit CLASS later (spine ~1159),
       so a .ld-toolkit class guard is still false at DOMContentLoaded and the bar
       double-mounted anyway. Guard on the id, which is present from the start. */
    if (document.getElementById('ld-toolkit')) return;
    /* 2026-09-16 (Mariya: the bottom bar vanishes on Cake ER). Cake ER's root
       is .cker, not .learn-page, so this bar never mounted there. Mount on
       either, and light the tab that matches the page you're on.
       2026-09-17 (Mariya: Serving should match the Studio too). Serving's root
       is .ldc-cake-calc (its content wrapper is .ldc-wrap) — no .learn-page/
       .cker — so it was the one tool with no bottom bar / sidebar, just a back
       link. Include it. 2026-09-17 (Mariya: "full match" for Pricing) — its root
       is .ldpc (content wrapper .ldp-wrap); its bespoke rail is retired
       (railShell early-returns), so it takes the shared sidebar too. */
    if (!document.querySelector('.learn-page, .cker, .ldc-cake-calc, .ldpc')) return;
    var onCakeEr = /cake-?er/.test(location.pathname);
    var TABS = [
      { lbl: 'This cake', href: '/pages/studio' },
      { lbl: 'Bookings', href: '/pages/studio?ldsp=bookings' },
      { lbl: 'Tools', href: '/pages/studio?ldsp=tools', on: !onCakeEr },
      { lbl: 'Cake ER', href: '/pages/cake-er', on: onCakeEr },
      /* Academy Build 01 (2026-10-08, brief 9.1): the courses door, for everyone */
      { lbl: 'courses', href: '/pages/courses' },
      { lbl: 'Settings', href: '/pages/studio?ldsp=account' }
    ];
    var nav = document.createElement('nav');
    nav.className = 'lh-tabbar';
    nav.setAttribute('aria-label', 'Toolkit');
    TABS.forEach(function (t) {
      var a = document.createElement('a');
      a.className = 'lh-tabbar__tab' + (t.on ? ' is-on' : '');
      a.href = t.href;
      a.textContent = t.lbl;
      nav.appendChild(a);
    });
    document.body.appendChild(nav);

    /* 2026-09-16 (Mariya: the desktop nav should MATCH the Studio, not be a
       phone bottom bar). On desktop, wrap the sidebar + the tool's content
       container in ONE centred flex shell - the exact structure the Studio
       uses (learn-toolkit-spine swap()), so the two line up perfectly. Wrapping
       the CONTAINER (not its children) is timing-proof: whatever the tool's own
       JS builds inside .learn-page/.cker stays put. In-flow means the sidebar
       starts below the storefront header on its own (no measuring). Below 900px
       it unwraps back to the <body> bottom bar. Pricing + Serving don't carry
       this bar, so they're untouched. */
    /* the content column the sidebar sits beside: .learn-page/.cker on the
       other tools, .ldc-wrap on Serving, .ldp-wrap on Pricing (each tool's
       .learn-subnav sibling is hidden) */
    var container = document.querySelector('.learn-page, .cker, .ldc-wrap, .ldp-wrap');
    /* 2026-10-11 (mobile pass): the members' LAYER'D bar now shows at every
       width; lift it out of the tool's content column so it heads the page */
    (function () {
      var ab = document.querySelector('header[data-ld-appbar]');
      if (ab && container && container.contains(ab)) container.parentNode.insertBefore(ab, container);
    })();
    /* 2026-10-11 (Mariya: "the toolbar on the left side still disappears for
       those two tools"). Batch Math and Cake ER are the two tools the Studio's
       wide Tools list opens as their own pages. They keep that same list down
       the left (the CSS shows it from 1200px only), the page you're on lit;
       the Studio's own tools open back inside the Studio with that tool open. */
    var toolHere = /batch-math/.test(location.pathname) ? 'batch' : (onCakeEr ? 'cakeer' : null);
    var toolList = null;
    if (toolHere) {
      toolList = document.createElement('aside');
      toolList.className = 'lh-toollist';
      toolList.setAttribute('aria-label', 'Tools');
      var tlH = document.createElement('p'); tlH.className = 'lh-toollist__h'; tlH.textContent = 'Tools';
      var tlS = document.createElement('p'); tlS.className = 'lh-toollist__sub'; tlS.textContent = 'Every calculator, on its own. Save any result into a cake file to keep it.';
      toolList.appendChild(tlH); toolList.appendChild(tlS);
      [
        ['Size', 'tap to size', '/pages/studio?ldsp=tools&tool=size', false],
        ['Structure', 'dowels & boards', '/pages/studio?ldsp=tools&tool=structure', false],
        ['Cost & price', 'price a cake', '/pages/studio?ldsp=tools&tool=cost', false],
        ['Quote', 'draft a quote', '/pages/studio?ldsp=tools&tool=quote', false],
        ['Recipes', 'scale a recipe', '/pages/studio?ldsp=tools&tool=recipes', false],
        ['Planner', 'build a timeline', '/pages/studio?ldsp=tools&tool=planner', false],
        ['Batch Math', 'Scale any recipe to your exact tiers and pan sizes.', '/pages/batch-math', toolHere === 'batch'],
        ['Cake ER', 'Something going wrong? The diagnosis is always free.', '/pages/cake-er', toolHere === 'cakeer']
      ].forEach(function (t) {
        var a = document.createElement('a');
        a.className = 'lh-toollist__item' + (t[3] ? ' is-on' : '');
        a.href = t[2];
        if (t[3]) a.setAttribute('aria-current', 'page');
        var n = document.createElement('span'); n.className = 'lh-toollist__name'; n.textContent = t[0];
        var u = document.createElement('span'); u.className = 'lh-toollist__line'; u.textContent = t[1];
        a.appendChild(n); a.appendChild(u);
        toolList.appendChild(a);
      });
    }
    function layoutToolNav() {
      if (!container) return;
      var wide = window.matchMedia && window.matchMedia('(min-width: 900px)').matches;
      var shell = document.querySelector('.lh-shell');
      if (wide && !shell) {
        shell = document.createElement('div');
        shell.className = 'lh-shell';
        container.parentNode.insertBefore(shell, container);
        /* 2026-10-11: the members' LAYER'D bar (rendered by learn-subnav, which
           some tools place inside their content column) sits ABOVE the tab row,
           exactly as on the Studio, not inside the column under it */
        var appbar = document.querySelector('header[data-ld-appbar]');
        if (appbar && container.contains(appbar)) shell.parentNode.insertBefore(appbar, shell);
        shell.appendChild(nav);         /* sidebar first (moves off <body>) */
        if (toolList) { shell.className += ' has-tools'; shell.appendChild(toolList); }
        shell.appendChild(container);   /* the content column */
      } else if (!wide && shell) {
        shell.parentNode.insertBefore(container, shell);
        document.body.appendChild(nav);
        if (toolList && toolList.parentNode) toolList.parentNode.removeChild(toolList);
        shell.parentNode.removeChild(shell);
      }
    }
    layoutToolNav();
    window.addEventListener('resize', layoutToolNav);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountToolTabbar);
  } else { mountToolTabbar(); }
  window.addEventListener('layerd:cakefile', function (e) {
    var d = (e && e.detail) || {};
    renderAll();
    if (!d.source || d.source === 'picker' || d.type === 'active') { ldPrimeStepState(); return; }
    var b = null;
    try { b = window.LdCakeFile && window.LdCakeFile.getActiveBooking(); } catch (e2) { b = null; }
    var done = saveCompletedItsStep(d.source, b);
    var key = d.source + ':' + ((b && b.id) || '-');
    var was = ldStepWasDone[key];
    ldStepWasDone[key] = done;
    if (done && !was) scrollToBar();
  });
  function scrollToBar() {
    var mount = document.querySelector('[data-ld-cakechip]');
    if (!mount) return;
    /* the ONE sanctioned viewport move (see navigateToSection's contract) */
    setTimeout(function () { try { navigateToSection(mount, { instant: true }); } catch (e) { } }, 0);
  }

  /* tool-driven setter: a tool passes its OWN formatted summary + origin
     (it knows its own units), and the chip renders that — so chip and tool
     can never disagree. Pass a falsy summary to clear back to the auto
     (shared-cake) path. `detached` toggles the outline "different cake"
     state. */
  window.LAYERD = window.LAYERD || {};
  window.LAYERD.components = window.LAYERD.components || {};
  window.LAYERD.components.setCakeChip = function (mount, summary, origin, detached) {
    mount = (typeof mount === 'string') ? document.getElementById(mount) : mount;
    if (!mount) return;
    if (summary) {
      mount.setAttribute('data-ld-summary', summary);
      mount.setAttribute('data-ld-origin', origin || 'your cake');
    } else {
      mount.removeAttribute('data-ld-summary');
      mount.removeAttribute('data-ld-origin');
    }
    if (typeof detached === 'boolean') mount.setAttribute('data-ld-detached', detached ? 'true' : 'false');
    build(mount);
  };
  /* S-4 (Simplification brief, 2026-08-03): "I was asked for my email three
     times… I am a logged-in Pro member. You already have my email." One
     answer to "is this person already a customer?", so every tool suppresses
     its lead capture on the same signal instead of each inventing a test. */
  window.LAYERD.components.isMember = function () {
    try {
      var n = document.querySelector('[data-lh-tier]');
      var t = n ? (n.getAttribute('data-lh-tier') || '') : '';
      if (t === 'premium' || t === 'academy') return true;
    } catch (e) { }
    /* the premium bundles are only ever served to members, so their presence
       is proof even when the theme attribute is missing (local previews) */
    var P = window.LAYERD && window.LAYERD.premium;
    return !!(P && (P.pricing || P.timeline || P.structure));
  };
  /* r172 (Mariya: "shouldn't the tool tell you to resume where you left
     off instead of sending you to the client file page"). The first
     unfinished step's URL, or null when the file is complete. This lives
     HERE because this module already owns PIPE_STEPS and stepHref - a
     caller deriving its own order would be free to drift from the dots
     the same file is drawing. */
  window.LAYERD.components.resumeHref = function (b) {
    if (!b || !b.id) return null;
    var st = null;
    try { st = nextStep(b); } catch (e) { return null; }
    if (!st) return null;                       /* complete - the file page IS the right place */
    var urls = {};
    try { urls = pipeUrls().urls; } catch (e2) { urls = {}; }
    try { return stepHref(st.key, urls[st.key], b.id); } catch (e3) { return null; }
  };
  window.LAYERD.components.refreshCakeChips = renderAll;
  window.LAYERD.components.navigateToSection = navigateToSection;
  window.LAYERD.components.holdScroll = holdScroll;
  window.LAYERD.components.readSharedCake = readShared;

  /* ---- Client Chip setter (§4.5): scope a chip to a client file. Pass the
     booking (or its id); the chip shows "Name · Date · Status" + the tier
     summary. Pass null to clear back to the plain cake chip. ---- */
  window.LAYERD.components.setClientChip = function (mount, booking, summary) {
    mount = (typeof mount === 'string') ? document.getElementById(mount) : mount;
    if (!mount) return;
    var b = (typeof booking === 'string') ? bookingById(booking) : booking;
    if (b) {
      mount.setAttribute('data-ld-client', (b.name || '').trim() || 'Cake order');
      mount.setAttribute('data-ld-status', statusOf(b));
      mount.setAttribute('data-ld-ci', String(b.colorIndex != null ? b.colorIndex : 0));
      mount.setAttribute('data-ld-date', niceDate(b.date));
      if (summary) mount.setAttribute('data-ld-summary', summary);
    } else {
      mount.removeAttribute('data-ld-client');
      mount.removeAttribute('data-ld-status');
      mount.removeAttribute('data-ld-ci');
      mount.removeAttribute('data-ld-date');
    }
    build(mount);
  };

  /* ---- the client picker (§4.5): one component, every pipeline tool. A
     compact <select> of files grouped by status (Booked + active inquiries
     up top), then "Just the math, no client". onPick(booking|null) fires on
     change; the default handler navigates the tool to ?booking=<id> (or clears
     it), reusing each tool's proven ?booking scoping. Renders nothing when
     there are no files, so anonymous single-tool use is untouched. ---- */
  function scopeToFileUrl(id) {
    var base = location.pathname;
    location.href = base + (id ? '?booking=' + encodeURIComponent(id) : '');
  }
  /* Toolkit Fix 01: seedSharedFromFile is RETIRED. Under the booking-scoped
     model the scratch slot (layerd-learn-tiers) belongs exclusively to
     anonymous work — writing a client's cake into it poisoned a visitor's
     scratch pad and baited the migration's adoption heuristic. Scoped tools
     now read the booking record directly (LdCakeFile.readCake). The export
     stays as a no-op so any stale caller can't crash. */
  function seedSharedFromFile() { /* retired — the booking record is the source of truth */ }
  window.LAYERD.components.scopeToFileUrl = scopeToFileUrl;
  window.LAYERD.components.seedSharedFromFile = seedSharedFromFile;
  window.LAYERD.components.readClientFiles = readBookings;
  /* Redesign r4e (Mariya, 2026-07-30): the pipeline moved INTO the cake
     bar - the chip holds the progress dots and the separate .ld-pipebar
     is retired ("I don't like the repetition"). The in-page Next links
     carry the forward pointer now, so no pill either. */
  function pipeDots(b) {
    if (!b) return null;
    var found, urls = {}, here = null;
    try { found = pipeUrls(); urls = found.urls; here = found.here; } catch (e) { }
    var row = el('span', { class: 'ld-pipedots', role: 'list', 'aria-label': 'Pipeline progress' });
    PIPE_STEPS.forEach(function (st) {
      var done = st.done(b);
      var href = null;
      try { href = stepHref(st.key, urls[st.key], b.id); } catch (e) { }
      var dot = el(href ? 'a' : 'span', {
        class: 'ld-pipedots__d' + (done ? ' is-done' : '') + (here === st.key ? ' is-here' : ''),
        role: 'listitem',
        title: st.label + (done ? ', done' : ', not yet'),
        'aria-label': st.label + (done ? ', done' : ', not yet')
      });
      if (href) dot.setAttribute('href', href);
      row.appendChild(dot);
    });
    return row;
  }

  /* the ONE next outstanding step — same PIPE_STEPS, same stepHref as the
     dots, so a dot and the pill can never contradict each other. Returns
     null when every step is done (nothing left to point at). */
  function nextStep(b) {
    if (!b) return null;
    for (var i = 0; i < PIPE_STEPS.length; i++) {
      try { if (!PIPE_STEPS[i].done(b)) return PIPE_STEPS[i]; } catch (e) { }
    }
    return null;
  }
  function nextPill(b) {
    var st = nextStep(b);
    /* 2026-08-03 (Mariya): "the client bar at the top should always redirect
       them to the client file (or any other outstanding steps), if they make
       changes after the fact."

       With every step done this returned null, so finishing a change in a
       tool left the bar with nowhere to go — she had just updated a client's
       flavours and the way back to that client's file was hers to find. The
       fallback is the file itself: nothing outstanding still means "here is
       the cake you were working on". When something IS outstanding (a stale
       batch sheet after a flavour change, say) the normal pill names it. */
    if (!st) return backToFilePill(b);
    var urls = {}, here = null, href = null;
    try { var f = pipeUrls(); urls = f.urls; here = f.here; } catch (e) { }
    /* never point at the page you are already on — that is a dead button.
       Mariya's words: the bar "will auto populate a next button once a step
       is saved", so on arrival there is nothing, and the moment the step is
       saved the pill appears naming the step after it. */
    if (here && here === st.key) return null;
    try { href = stepHref(st.key, urls[st.key], b.id); } catch (e) { }
    var pill = el(href ? 'a' : 'span', {
      class: 'ld-cakechip__next', text: 'Next: ' + st.label + ' \u2192'
    });
    if (href) pill.setAttribute('href', href);
    return pill;
  }
  /* the "everything is in" pill — back to the file this tool is scoped to.
     Suppressed on the Planner itself (you are already there). */
  function backToFilePill(b) {
    if (!b || !b.id) return null;
    var urls = {}, here = null, href = null;
    try { var f = pipeUrls(); urls = f.urls; here = f.here; } catch (e) { }
    if (here === 'planner') return null;
    try { href = stepHref('planner', urls.planner, b.id); } catch (e) { }
    if (!href) return null;
    var name = ((b.name || '') + '').trim();
    var pill = el('a', {
      class: 'ld-cakechip__next ld-cakechip__next--back',
      href: href,
      text: 'Back to ' + (name || 'the file') + ' →'
    });
    return pill;
  }

  /* ---- the file popover (design addendum: "Tapping change opens the
     popover of active files") — replaces the always-on <select>. ---- */
  function shortDate(iso) {
    var s = niceDate(iso);
    return s ? s.replace(/,\s*\d{4}$/, '') : '';
  }
  function activeFiles(currentId) {
    var todayLocal = (function () {
      var d = new Date();
      return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
    })();
    /* past delivery = nothing left to do in the tools (Mariya 2026-07-21);
       the file this page is scoped to always stays listed */
    return readBookings().filter(function (b) {
      if (statusOf(b) === 'archived') return false;
      if (b.date && b.date < todayLocal && b.id !== (currentId || '')) return false;
      return true;
    });
  }
  function unattachedMember() {
    if (!window.LdCakeFile) return false;
    return activeFiles('').length > 0;
  }
  /* the funnel state: the cake-file layer is here, a cake has been sized,
     and there is no file anywhere yet. Deliberately NOT tier-gated — the
     free tier's one-booking cap is the upsell and carries its own copy;
     stopping someone before they have a single file would be the wrong
     end of the funnel. */
  function startFileOffer() {
    var F = window.LdCakeFile;
    if (!F || !F.createInquiry) return false;
    if (activeFiles('').length) return false;
    /* r201: activeFiles() drops archived and past-dated files, so a free
       member whose event has passed read as having none - the chip
       offered, and the store now refuses. The cap counts every booking,
       so ask it directly. The Planner's cap card carries the pitch; a
       chip that cannot work does not belong in the bar. */
    if (F.atCap && F.atCap()) return false;
    return !!readShared();
  }
  /* create → adopt the cake on screen → let the normal chip re-render show
     the scoped bar. Reuses LdCakeFile end to end; Serving's own
     wireStartFile keeps its copy of this flow for the in-results callout. */
  function wireStartFileChip(node, btn) {
    var open = false;
    btn.addEventListener('click', function () {
      if (open) return;
      open = true;
      btn.hidden = true;
      var input = el('input', {
        class: 'ld-cakechip__startname', type: 'text', maxlength: '60',
        placeholder: 'Client or event name', 'aria-label': 'Client or event name'
      });
      /* r44: the event date is asked for HERE too — a file started from
         Serving used to reach Confirm booking with no date, and the only
         hint was a disabled button. */
      var dateI = el('input', {
        class: 'ld-cakechip__startdate', type: 'date', 'aria-label': 'Event date'
      });
      var save = el('button', { class: 'ld-cakechip__change ld-cakechip__startgo', type: 'button', text: 'start' });
      var cancel = el('button', { class: 'ld-cakechip__startcancel', type: 'button', text: 'cancel', 'aria-label': 'Cancel starting a client file' });
      var row = el('span', { class: 'ld-cakechip__startrow' }, [input, dateI, save, cancel]);
      btn.parentNode.appendChild(row);
      try { input.focus(); } catch (e) { }
      function close() { row.remove(); btn.hidden = false; open = false; }
      cancel.addEventListener('click', close);
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); save.click(); }
        else if (e.key === 'Escape') close();
      });
      save.addEventListener('click', function () {
        var F = window.LdCakeFile;
        var nm = (input.value || '').trim();
        if (!nm) { input.setAttribute('placeholder', 'Give the inquiry a name first'); try { input.focus(); } catch (e) { } return; }
        var dt = (dateI.value || '').trim();
        if (!dt) { dateI.classList.add('is-missing'); save.textContent = 'add the date'; try { dateI.focus(); } catch (e) { } return; }
        if (!F || !F.createInquiry) return;
        /* the cake is read HERE, not captured at build time: build() nulls
           its `cake` var whenever the tool pushed its own summary, and she
           may have changed the stack between the render and this click.
           Without this the new file silently kept createInquiry's default
           2-tier stack — caught in testing: the bar showed 14"+12" and the
           file was written 8"+6". */
        var shared = readShared();
        var b = F.createInquiry(nm, 'serving', { date: dt });
        /* r201: unreachable while startFileOffer gates the chip, but if a
           file is created in another tab between render and click, say the
           real reason rather than "couldn't start". */
        if (!b) { save.textContent = (F.atCap && F.atCap()) ? 'one file at a time' : 'couldn’t start'; return; }
        /* adopt the cake that is on screen — base-first, the store contract */
        if (shared && shared.tiers && shared.tiers.length && F.writeCake) {
          try {
            /* r154 (her repro: chip-started file said wedding on a party
               session): the slot carries how the cake is sliced now -
               adopt that fact along with the sizes. A slot written by a
               pre-r154 bundle has no occasion; the file keeps its default
               rather than guessing. */
            F.writeCake(shared.tiers.slice().sort(function (x, y) { return (y.d || 0) - (x.d || 0); })
              .map(function (t) { return { d: t.d, h: t.h || 4, shape: t.shape || 'round' }; }), 'serving',
              (shared.occasion === 'party' || shared.occasion === 'wedding') ? { occasion: shared.occasion } : null);
          } catch (e) { /* the file exists either way */ }
        }
        renderAll();   /* the chip re-reads and comes back scoped */
      });
    });
  }
  function fileSubLine(b) {
    var bits = [];
    try {
      var spec = (window.LdCakeFile && window.LdCakeFile.specOf) ? window.LdCakeFile.specOf(b) : (b.tierSpec || []);
      var t = summarizeFileTiers(spec);
      if (t) bits.push(t);
    } catch (e) { }
    var d = shortDate(b.date);
    if (d) bits.push(d);
    return bits.join(' \u00b7 ');
  }
  function filePopover(currentId) {
    var files = activeFiles(currentId);
    if (!files.length) return null;
    var pop = el('div', { class: 'ld-filepop', role: 'dialog', 'aria-label': 'Client files' });
    pop.appendChild(el('div', { class: 'ld-filepop__h', text: 'Client files' }));
    var order = ['booked', 'quoted', 'inquiry'];
    var sorted = files.slice().sort(function (a, b) {
      var oa = order.indexOf(statusOf(a)), ob = order.indexOf(statusOf(b));
      if (oa !== ob) return (oa < 0 ? 99 : oa) - (ob < 0 ? 99 : ob);
      return (a.date || '9999') < (b.date || '9999') ? -1 : 1;
    });
    function pick(b) {
      if (window.LdCakeFile) window.LdCakeFile.setActiveBooking(b ? b.id : null, 'picker');
      scopeToFileUrl(b ? b.id : null);
    }
    /* r75 (Mariya): "add an option to delete clients directly from the
       client chip ... a warning before deletion". Two taps, planner-style:
       the first arms ("Delete <name>? Tap \u00d7 again"), four seconds to
       change your mind, the second deletes. Cloud sync turns the removal
       into a tombstone on its next pass, so it stays deleted everywhere. */
    function deleteBooking(b) {
      try {
        var raw = JSON.parse(localStorage.getItem('layerd-academy-bookings'));
        if (!raw || !raw.bookings) return false;
        raw.bookings = raw.bookings.filter(function (x) { return x.id !== b.id; });
        localStorage.setItem('layerd-academy-bookings', JSON.stringify(raw));
        if (window.LdCakeFile && window.LdCakeFile.getActiveBooking) {
          var ab = window.LdCakeFile.getActiveBooking();
          if (ab && ab.id === b.id) window.LdCakeFile.setActiveBooking(null, 'picker');
        }
        try { window.dispatchEvent(new CustomEvent('layerd:cakefile', { detail: { type: 'file', source: 'bar' } })); } catch (e2) { }
        return true;
      } catch (e) { return false; }
    }
    sorted.forEach(function (b) {
      var on = b.id === currentId;
      var sub = el('span', { class: 'ld-filepop__s', text: fileSubLine(b) });
      var row = el('button', { class: 'ld-filepop__r' + (on ? ' is-on' : ''), type: 'button' }, [
        el('span', { class: 'ld-filepop__t' }, [
          el('span', { class: 'ld-filepop__n', text: (b.name || '').trim() || 'Cake order' }),
          sub
        ]),
        el('span', { class: 'ld-filepop__c', 'aria-hidden': 'true', text: on ? '\u2713' : '' })
      ]);
      if (on) row.setAttribute('aria-current', 'true');
      row.addEventListener('click', function () { pick(b); });
      var armed = false, disarm = null;
      var x = el('button', { class: 'ld-filepop__del', type: 'button', text: '\u00d7',
        'aria-label': 'Delete ' + ((b.name || '').trim() || 'this client file') });
      x.addEventListener('click', function (ev) {
        ev.stopPropagation();   /* never pick the file you're deleting */
        if (!armed) {
          armed = true;
          x.classList.add('is-armed');
          sub.textContent = 'Delete ' + ((b.name || '').trim() || 'this file') + '? Everything on the file goes with it \u2014 tap \u00d7 again';
          sub.classList.add('ld-filepop__s--warn');
          disarm = setTimeout(function () {
            armed = false;
            x.classList.remove('is-armed');
            sub.textContent = fileSubLine(b);
            sub.classList.remove('ld-filepop__s--warn');
          }, 4000);
          return;
        }
        clearTimeout(disarm);
        if (deleteBooking(b)) {
          if (b.id === currentId) { scopeToFileUrl(null); return; }
          closePop();
          renderAll();
        } else {
          sub.textContent = 'Couldn\u2019t delete \u2014 try it from the Planner';
        }
      });
      var wrap = el('div', { class: 'ld-filepop__rowwrap' }, [row, x]);
      pop.appendChild(wrap);
    });
    var none = el('button', { class: 'ld-filepop__r ld-filepop__r--none' + (currentId ? '' : ' is-on'), type: 'button' }, [
      el('span', { class: 'ld-filepop__t' }, [
        el('span', { class: 'ld-filepop__n', text: 'Just the math' }),
        el('span', { class: 'ld-filepop__s', text: 'No client file' })
      ]),
      el('span', { class: 'ld-filepop__c', 'aria-hidden': 'true', text: currentId ? '' : '\u2713' })
    ]);
    none.addEventListener('click', function () { pick(null); });
    pop.appendChild(none);
    /* r21 (Mariya): "letting someone open a new booking from that client
       bar ... there could also be a + new booking button". The bar already
       answers "which file am I on", so "start one" belongs in the same
       place - and it means a file can begin from any tool instead of a
       trip back to the Planner. Uses the file layer's own createInquiry,
       so the new file is identical to one started on the Planner. */
    /* r201: LEAK 1. This row called createInquiry with no tier check, on
       every tool page, so a free member with her one file could open the
       picker anywhere and start a second. At the cap it becomes the offer
       instead - same row shape, existing classes, no new CSS. */
    var Fcap = window.LdCakeFile;
    if (Fcap && Fcap.atCap && Fcap.atCap()) {
      var capRow = el('a', { class: 'ld-filepop__r ld-filepop__new',
        href: '/pages/toolkit' }, [
        el('span', { class: 'ld-filepop__t' }, [
          el('span', { class: 'ld-filepop__n', text: 'One file at a time' }),
          el('span', { class: 'ld-filepop__s',
            text: 'Toolkit Pro keeps every inquiry open at once.' })
        ])
      ]);
      pop.appendChild(capRow);
      return pop;
    }
    var mk = el('button', { class: 'ld-filepop__r ld-filepop__new', type: 'button' }, [
      el('span', { class: 'ld-filepop__t' }, [
        el('span', { class: 'ld-filepop__n', text: '+ New booking' }),
        el('span', { class: 'ld-filepop__s', text: 'Start a client file from here' })
      ])
    ]);
    /* r44 (Mariya, 2026-08-12): "it didn't ask me to enter an event date
       anywhere so that was missing from the file. This should be mandatory."
       window.prompt() could only ever ask ONE thing, which is exactly how a
       file reached the Confirm-booking gate with no date and a deposit she
       couldn't save. Replaced with a two-field panel that refuses to submit
       without both. */
    mk.addEventListener('click', function () {
      var F = window.LdCakeFile;
      if (!F || !F.createInquiry) { location.href = '/pages/planner?ldview=new-inquiry'; return; }
      if (pop.querySelector('.ld-filepop__form')) return;
      mk.hidden = true;
      var nmI = el('input', { class: 'ld-filepop__in', type: 'text', maxlength: '60',
        placeholder: 'Client or event name', 'aria-label': 'Client or event name' });
      var dtI = el('input', { class: 'ld-filepop__in', type: 'date',
        'aria-label': 'Event date' });
      var msg = el('p', { class: 'ld-filepop__err', role: 'alert' });
      var go = el('button', { class: 'ld-filepop__go', type: 'button', text: 'Start the file' });
      var cx = el('button', { class: 'ld-filepop__cx', type: 'button', text: 'cancel' });
      var form = el('div', { class: 'ld-filepop__form' }, [
        el('span', { class: 'ld-filepop__flabel', text: 'NEW CLIENT FILE' }),
        nmI, el('span', { class: 'ld-filepop__flabel', text: 'EVENT DATE' }), dtI,
        msg, el('div', { class: 'ld-filepop__frow' }, [go, cx])
      ]);
      pop.appendChild(form);
      try { nmI.focus(); } catch (e) { }
      function close() { form.remove(); mk.hidden = false; }
      cx.addEventListener('click', close);
      form.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); go.click(); }
        else if (e.key === 'Escape') { e.preventDefault(); close(); }
      });
      go.addEventListener('click', function () {
        var nm = (nmI.value || '').trim();
        var dt = (dtI.value || '').trim();
        if (!nm) { msg.textContent = 'Give the file a name.'; try { nmI.focus(); } catch (e) { } return; }
        /* the date is the mandatory half — without it the file cannot ever
           be confirmed as a booking, and nothing downstream can be planned. */
        if (!dt) { msg.textContent = 'Add the event date. The plan and the booking both need it.'; try { dtI.focus(); } catch (e) { } return; }
        var made = F.createInquiry(nm, 'bar', { date: dt });
        if (!made) { location.href = '/pages/planner?ldview=new-inquiry'; return; }
        scopeToFileUrl(made.id);                    /* land scoped to it */
      });
    });
    pop.appendChild(mk);
    return pop;
  }
  /* ONE set of document listeners for the whole page — attaching them per
     render leaked a pair on every re-render (and the bar re-renders on
     every save). */
  var openPop = null, openBtn = null;
  /* r111 (Mariya, 2026-08-25): "the dropdown goes behind the quote box".
     The chip row under a headband is `position:relative; z-index:1`, which
     makes it a STACKING CONTEXT — so the popover's own z-index:50 can only
     compete inside it, and any later sibling with a higher z-index (on
     Pricing, the sticky quote strip at 32) paints over the whole row. The
     row is lifted only while a popover is open, so it never rides over a
     sticky subnav the rest of the time. */
  function rowOf(node) {
    return node && node.closest ? node.closest('.ld-cakechip-row') : null;
  }
  function closePop() {
    var row = rowOf(openPop) || rowOf(openBtn);
    if (row) row.classList.remove('is-popping');
    if (openPop && openPop.parentNode) openPop.parentNode.removeChild(openPop);
    if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
    openPop = null; openBtn = null;
  }
  document.addEventListener('click', function (ev) {
    if (!openPop) return;
    /* r59: "click outside to close" means the USER clicked outside — a
       programmatic .click() (Pricing's always-live engine pressing its
       hidden Calc button was the one that bit) must never dismiss an open
       popover. Synthetic events carry isTrusted === false. */
    if (ev.isTrusted === false) return;
    var t = ev.target;
    if ((openPop.contains && openPop.contains(t)) || (openBtn && openBtn.contains && openBtn.contains(t))) return;
    closePop();
  });
  document.addEventListener('keydown', function (ev) {
    if (ev.key === 'Escape' || ev.keyCode === 27) closePop();
  });
  function wirePopover(host, btn, currentId) {
    btn.addEventListener('click', function (ev) {
      ev.stopPropagation();
      if (openBtn === btn) { closePop(); return; }
      closePop();
      var pop = filePopover(currentId);
      if (!pop) return;
      host.appendChild(pop);
      openPop = pop; openBtn = btn;
      var row = rowOf(pop) || rowOf(btn);
      if (row) row.classList.add('is-popping');
      btn.setAttribute('aria-expanded', 'true');
    });
  }

  /* the old always-on <select> is retired — one bar, one popover, every
     page. Kept as a null factory so a stale cached tool bundle that still
     calls it simply renders nothing instead of a second file control. */
  window.LAYERD.components.clientPicker = function () { return null; };

  /* ---------- Toolkit Fix 01 Phase 2: the pipeline bar ----------
     "Working on: [name] · step dots · Next →" under the subnav, on every
     tool page at once. Reads ONLY the cake-file layer; writes nothing.
     Tool URLs are discovered from the subnav's own tabs, so the bar
     follows whatever pages the theme settings point at. */
  /* the chip mount's own data-ld-tool -> its pipeline step. "recipes" is the
     flavours step; the Planner has no chip (you are already on the file). */
  var TOOL_STEP = {
    serving: 'serving', pricing: 'pricing', structure: 'structure',
    recipes: 'flavours', batch: 'batch', 'batch-math': 'batch'
  };
  var PIPE_STEPS = [
    { key: 'serving', label: 'Serving', match: /serving/i,
      done: function (b) { return !!(b.cake && b.cake.updatedAt > 0); } },
    /* pricing sits with SELLING, not production (2026-07-20): the quote
       happens at inquiry — size it, price it, book on it; everything after
       the booking is final details */
    { key: 'pricing', label: 'Quote', match: /pricing/i,
      done: function (b) { return !!(b.quotes && b.quotes.length); } },
    { key: 'structure', label: 'Structure', match: /structure/i,
      /* CONFIRMED, not merely saved — a size change resets the approval,
         and the suite bar must agree with the file's outstanding list
         ("All set" over "1 outstanding" contradiction, Mariya 2026-07-20).
         Files predating the flag are grandfathered by normalizeBooking. */
      done: function (b) { return !!b.structureConfirmed; } },
    /* r183 (2026-08-29, Mariya: "when you complete structure, the next
       button says flavours, but the tool navigation bar says recipes").
       RECIPES wins, for one reason that settles it: /pages/menu-pricing is
       already the client-facing FLAVOURS menu, so the word was naming two
       different things in one business. It is also the more honest label -
       the page free tier is three saved recipes, and Pro/Academy is the
       recipe book; "Flavours" describes only the combos section this step
       happens to land on. The KEY stays 'flavours' - it is written into
       saved state and the #arcpCombos deep link; only the label moves. */
    { key: 'flavours', label: 'Recipes', match: /recipe/i,
      done: function (b) {
        /* done when every tier carries a flavour (assigning a Signature
           Combination writes tierSpec[i].flavour) — OR the baker went
           straight to Batch Math, so a one-flavour cake can skip this
           step without the bar holding it hostage (Mariya, 2026-07-16) */
        var F = window.LdCakeFile;
        var spec = (F && F.specOf) ? F.specOf(b) : (b.tierSpec || []);
        if (spec.length && spec.every(function (t) { return t && (t.flavour || '').trim(); })) return true;
        if (!b.batchSheet) return false;
        return Object.keys(b.batchSheet).some(function (k) { return k !== 'sc' && k.charAt(0) !== '_'; });
      } },
    { key: 'batch', label: 'Batch Math', match: /batch/i,
      done: function (b) {
        /* pan-scaling ('sc') is the standalone utility — saving it alone
           must not mark the Batch Math step done (Mariya, 2026-07-16) */
        if (!b.batchSheet) return false;
        /* 2026-08-03 (Mariya): a STALE sheet is not done. Changing the
           flavours or the sizes reopens Batch on the client file, but the
           suite bar went on showing the dot filled — so the one place that
           is supposed to tell you what is outstanding disagreed with the
           file, and "re-run Batch Math" was invisible from the tool you had
           just saved in. Same rule the file uses. */
        if (b.batchSheet._stale) return false;
        return Object.keys(b.batchSheet).some(function (k) { return k !== 'sc' && k.charAt(0) !== '_'; });
      } },
    { key: 'planner', label: 'Deposit', match: /planner|timeline/i,
      done: function (b) { return statusOf(b) === 'booked'; } }
  ];
  /* 2026-08-11 — THE LIST ABOVE IS NOW ONLY A FALLBACK.
     learn-cakefile.js owns the one pipeline definition that the chip dots,
     the Planner row dashes and the file page's counter all read, so the
     three can no longer disagree about what "done" means (they disagreed on
     four of six steps, including a dash that could never fill). cakefile.js
     is loaded at line 110 of sections/learn-subnav.liquid and this file at
     116 — both `defer`, which preserves document order, so LdCakeFile is
     always defined by the time this runs. The literal above survives only
     so a page that somehow loads components without cakefile still renders
     dots instead of throwing. Change steps THERE, never here. */
  if (window.LdCakeFile && window.LdCakeFile.PIPELINE) PIPE_STEPS = window.LdCakeFile.PIPELINE;
  function pipeUrls() {
    var map = {}, here = null;
    var tabs = document.querySelectorAll('.learn-subnav__tab');
    for (var i = 0; i < tabs.length; i++) {
      var a = tabs[i], t = (a.textContent || '').trim();
      for (var s = 0; s < PIPE_STEPS.length; s++) {
        var st = PIPE_STEPS[s];
        if (!map[st.key] && st.match.test(t)) {
          map[st.key] = a.getAttribute('href') || '';
          if (a.classList.contains('is-active') || a.getAttribute('aria-current') === 'page') here = st.key;
          else if (here === null && map[st.key] && map[st.key] !== '#') {
            /* fallback when the theme misses is-active: match the path */
            try { if (new URL(map[st.key], location.href).pathname === location.pathname) here = st.key; } catch (e) { }
          }
        }
      }
    }
    return { urls: map, here: here };
  }
  function pipeGa(step) {
    try {
      var host = document.querySelector('[data-lh-tier]');
      var tier = (host && host.getAttribute('data-lh-tier')) || 'visitor';
      if (window.lhTrack) window.lhTrack('learn_hub_cta_clicked', { tool_name: 'pipebar', cta_type: 'next_' + step, user_tier: tier });
    } catch (e) { }
  }
  function stepHref(key, url, id) {
    if (!url || url === '#') return null;
    /* the Planner opens the FILE page; tools open scoped to the client */
    var h = url + (url.indexOf('?') >= 0 ? '&' : '?') + (key === 'planner' ? 'file=' : 'booking=') + encodeURIComponent(id);
    /* the Flavours step lands straight on the Signature Combinations
       section of the Recipe Library — the page's own hash handler scrolls
       there once the section has rendered */
    if (key === 'flavours') h += '#arcpCombos';
    return h;
  }
  function buildPipebar() {
    /* r4e: RETIRED as a bar - the dots live in the cake bar/chip now.
       This function stays as the cleanup + refresh hook: it removes any
       bar an older cached asset drew, zeroes the height var every sticky
       element offsets by, and re-renders the chips so their dots track
       the file's progress. */
    var old = document.querySelector('.ld-pipebar');
    if (old) old.remove();
    try { document.documentElement.style.setProperty('--ld-pipebar-h', '0px'); } catch (e) { }
    try { window.LAYERD.components.refreshCakeChips(); } catch (e) { }
    return;
  }
  function buildPipebarRetired() {
    var F = window.LdCakeFile;
    var host = document.querySelector('.learn-subnav');
    if (!F || !host || !host.parentNode) return;
    var old = document.querySelector('.ld-pipebar');
    var b = F.getActiveBooking();
    if (!b) {
      if (old) old.remove();
      try { document.documentElement.style.setProperty('--ld-pipebar-h', '0px'); } catch (e) { }
      return;
    }

    var found = pipeUrls(), urls = found.urls, here = found.here;
    var name = (b.name || '').trim() || 'Cake order';
    var doneN = 0;
    PIPE_STEPS.forEach(function (st) { if (st.done(b)) doneN++; });

    var whoHref = stepHref('planner', urls.planner, b.id);
    /* CO#1 B2.2 amended live (Mariya): the bar KEEPS the client's name —
       her call reverses the CO's "segments only" line. The de-dup happens
       at the picker instead: scoped pages hide the standalone dropdown. */
    var who = el(whoHref ? 'a' : 'span', { class: 'ld-pipebar__who' }, [
      el('span', { class: 'ld-pipebar__lbl', text: 'Working on' }),
      (function () { var d = el('span', { class: 'ld-pipebar__cdot', 'aria-hidden': 'true' }); d.style.setProperty('--c', cakeHex(b.colorIndex)); return d; })(),
      el('span', { class: 'ld-pipebar__name', text: name })
    ]);
    if (whoHref) who.setAttribute('href', whoHref);

    var steps = el('div', { class: 'ld-pipebar__steps', role: 'list', 'aria-label': 'Pipeline progress' });
    PIPE_STEPS.forEach(function (st) {
      var done = st.done(b);
      var href = stepHref(st.key, urls[st.key], b.id);
      var cls = 'ld-pipebar__step' + (done ? ' is-done' : '') + (here === st.key ? ' is-here' : '');
      var dot = el(href ? 'a' : 'span', {
        class: cls, role: 'listitem',
        title: st.label + (done ? ', done' : ', not yet'),
        'aria-label': st.label + (done ? ', done' : ', not yet') + (here === st.key ? ', current page' : '')
      });
      if (href) dot.setAttribute('href', href);
      steps.appendChild(dot);
    });

    /* Next = the FIRST unfinished stage, honestly. If that stage is the page
       you're standing on, the bar says "finish this step" instead of pointing
       away — pointing forward while the current step was unfinished sent the
       user ping-ponging Serving↔Structure (Mariya, 2026-07-16). The pill
       flips to "Next: … →" live the moment the step's save lands. */
    var first = null;
    for (var i2 = 0; i2 < PIPE_STEPS.length; i2++) {
      if (!PIPE_STEPS[i2].done(b)) { first = PIPE_STEPS[i2]; break; }
    }
    var nextEl;
    if (first && first.key === here && here === 'planner') {
      /* the Planner IS the destination — never nag "finish this step" there;
         confirming the booking is its own affordance on the file page */
      nextEl = null;
    } else if (first && first.key === here) {
      nextEl = el('span', { class: 'ld-pipebar__next ld-pipebar__next--here', text: 'Finish this step first' });
    } else if (first) {
      var nh = stepHref(first.key, urls[first.key], b.id);
      nextEl = el(nh ? 'a' : 'span', { class: 'ld-pipebar__next', text: 'Next: ' + first.label + ' →' });
      if (nh) {
        nextEl.setAttribute('href', nh);
        (function (k) { nextEl.addEventListener('click', function () { pipeGa(k); }); })(first.key);
      }
    } else {
      nextEl = el(whoHref ? 'a' : 'span', { class: 'ld-pipebar__next ld-pipebar__next--done', text: 'All set ✓' });
      if (whoHref) nextEl.setAttribute('href', whoHref);
    }

    var bar = el('div', { class: 'ld-pipebar' }, [
      who, steps,
      el('span', { class: 'ld-pipebar__count', text: doneN + ' of ' + PIPE_STEPS.length }),
      nextEl
    ].filter(Boolean));
    if (old) old.remove();
    /* round nineteen (Mariya): the bar is sticky, but sticky only holds
       while its PARENT is in view — inside the short subnav section it
       scrolled away instantly. Insert it as a child of the tall
       .learn-page wrapper (right after the branch holding the subnav)
       so the Next pill follows the user down the whole page. */
    var page = host.closest('.learn-page');
    if (page) {
      var arm = host;
      while (arm.parentNode && arm.parentNode !== page) arm = arm.parentNode;
      page.insertBefore(bar, arm.nextSibling);
    } else {
      host.parentNode.insertBefore(bar, host.nextSibling);
    }
    /* tool jump bars stack UNDER the pipebar via this variable */
    try { document.documentElement.style.setProperty('--ld-pipebar-h', bar.offsetHeight + 'px'); } catch (e) { }
    initPipebarStick(bar);
  }

  /* round twenty (Mariya): on the LIVE theme, CSS sticky dies — section
     wrappers clip it and the theme's own pinned header owns the top edge.
     A JS "stuck" mode pins the bar with position:fixed instead, seated
     just BELOW whatever the theme keeps pinned up there, with a spacer
     holding the layout so nothing jumps. */
  var stickWired = false;
  function themeHeaderBottom(bar) {
    /* whatever fixed/sticky element covers the top of the viewport that
       isn't ours — its bottom edge is where the bar should pin */
    if (!document.elementsFromPoint) return 0;
    var els = document.elementsFromPoint(Math.floor(window.innerWidth / 2), 2) || [];
    for (var i = 0; i < els.length; i++) {
      var e2 = els[i];
      if (e2 === bar || bar.contains(e2) || e2 === document.documentElement || e2 === document.body) continue;
      var cs = getComputedStyle(e2);
      if ((cs.position === 'fixed' || cs.position === 'sticky') && e2.getBoundingClientRect().top <= 2) {
        return Math.max(0, Math.round(e2.getBoundingClientRect().bottom));
      }
    }
    return 0;
  }
  function initPipebarStick(bar) {
    var spacer = document.querySelector('.ld-pipebar-spacer');
    if (!spacer) {
      spacer = document.createElement('div');
      spacer.className = 'ld-pipebar-spacer';
      spacer.style.display = 'none';
    }
    bar.parentNode.insertBefore(spacer, bar);
    function currentBar() { return document.querySelector('.ld-pipebar'); }
    function update() {
      var b2 = currentBar();
      var sp = document.querySelector('.ld-pipebar-spacer');
      if (!b2 || !sp) return;
      var wasStuck = b2.classList.contains('is-stuck');
      var off = themeHeaderBottom(b2);
      /* natural flow position: the bar's own top when in flow, the
         spacer's top while the bar is pinned out of flow */
      var natTop = wasStuck ? sp.getBoundingClientRect().top : b2.getBoundingClientRect().top;
      if (natTop < off) {
        if (!wasStuck) {
          sp.style.height = b2.offsetHeight + 'px';
          sp.style.display = 'block';
          b2.classList.add('is-stuck');
        }
        b2.style.top = off + 'px';
        try { document.documentElement.style.setProperty('--ld-pipebar-h', (off + b2.offsetHeight) + 'px'); } catch (e) { }
      } else if (wasStuck) {
        b2.classList.remove('is-stuck');
        b2.style.top = '';
        sp.style.display = 'none';
        try { document.documentElement.style.setProperty('--ld-pipebar-h', b2.offsetHeight + 'px'); } catch (e) { }
      }
    }
    if (!stickWired) {
      stickWired = true;
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
    }
    update();
  }
  function initPipebar() {
    buildPipebar();
    if (window.LdCakeFile && window.LdCakeFile.onChange) {
      window.LdCakeFile.onChange(function () { buildPipebar(); });
    }
  }

  function initAll() { renderAll(); initPipebar(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
})();

/* ============================================================
   r23 (2026-07-31) — TOOLKIT SETTINGS.
   Mariya: "what if we move the inch/cm to a settings thing at the
   front of the toolkit, which applies to all tools? is that
   smarter? same with currency".
   It is smarter, and it costs almost nothing, because the tools
   ALREADY share their storage keys - Serving, Structure, Batch
   Math and Pricing all read 'layerd-learn-units'. So this panel
   does not invent a new system; it writes the same keys the tools
   already obey, and each page's own inline control keeps working
   and keeps writing back. ONE source of truth, two ways in.
   Mount: <div data-ld-settings></div>
   ============================================================ */
(function initLdSettings() {
  /* this block sits OUTSIDE the module closure, so the shared el() helper
     is not in scope here (caught in verification - it threw on first
     render). Its own tiny builder, same signature, no dependency. */
  function el(tag, attrs, kids) {
    var n = document.createElement(tag);
    attrs = attrs || {};
    Object.keys(attrs).forEach(function (k) {
      if (k === 'text') n.textContent = attrs[k];
      else if (k === 'class') n.className = attrs[k];
      else n.setAttribute(k, attrs[k]);
    });
    (kids || []).forEach(function (c) { if (c) n.appendChild(c); });
    return n;
  }
  var UNITS_KEY = 'layerd-learn-units';      /* 'in' | 'cm'  - serving/structure/bm/pricing */
  var BOWL_KEY = 'layerd-bm-bowl';           /* '5' | '6' ('7' = legacy alias, r86) - batch math */
  var CUR_KEY = 'layerd-learn-currency';     /* ISO 4217 code ('US'|'CA' legacy) - pricing reads this at load */

  function get(k, dflt) {
    try { return localStorage.getItem(k) || dflt; } catch (e) { return dflt; }
  }
  function set(k, v) {
    try { localStorage.setItem(k, v); } catch (e) { }
    /* the same events the tools already listen for, so an open tab follows */
    try { window.dispatchEvent(new Event('layerd:cake-updated')); } catch (e2) { }
    try { window.dispatchEvent(new StorageEvent('storage', { key: k })); } catch (e3) { }
  }
  function seg(label, hint, key, opts, dflt) {
    var row = el('div', { class: 'ld-settings__row' });
    row.appendChild(el('div', { class: 'ld-settings__text' }, [
      el('span', { class: 'ld-settings__lbl', text: label }),
      el('span', { class: 'ld-settings__hint', text: hint })
    ]));
    var track = el('span', { class: 'ld-settings__seg', role: 'group', 'aria-label': label });
    var cur = get(key, dflt);
    opts.forEach(function (o) {
      var b = el('button', {
        class: 'ld-settings__segb' + (cur === o.v ? ' is-on' : ''), type: 'button',
        text: o.t, 'aria-pressed': cur === o.v ? 'true' : 'false'
      });
      b.addEventListener('click', function () {
        set(key, o.v);
        Array.prototype.forEach.call(track.querySelectorAll('.ld-settings__segb'), function (x) {
          var on = x === b;
          x.classList.toggle('is-on', on);
          x.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      });
      track.appendChild(b);
    });
    row.appendChild(track);
    return row;
  }
  /* r54 (Mariya): the currency setting was a two-button segment, US | CA. The
     Pricing tool quotes in any currency now (r197, ISO codes), so this becomes
     a full dropdown that writes the same key. Codes are ISO 4217, symbol only,
     nothing is ever converted. This list mirrors pricing-master.json's
     currencies - if you add one there, add it here. */
  var CURRENCIES = [
    { code: 'USD', label: 'US dollar' }, { code: 'CAD', label: 'Canadian dollar' },
    { code: 'GBP', label: 'British pound' }, { code: 'EUR', label: 'Euro' },
    { code: 'AUD', label: 'Australian dollar' }, { code: 'NZD', label: 'New Zealand dollar' },
    { code: 'CHF', label: 'Swiss franc' }, { code: 'SEK', label: 'Swedish krona' },
    { code: 'NOK', label: 'Norwegian krone' }, { code: 'DKK', label: 'Danish krone' },
    { code: 'PLN', label: 'Polish zloty' }, { code: 'CZK', label: 'Czech koruna' },
    { code: 'HUF', label: 'Hungarian forint' }, { code: 'RON', label: 'Romanian leu' },
    { code: 'MXN', label: 'Mexican peso' }, { code: 'BRL', label: 'Brazilian real' },
    { code: 'CLP', label: 'Chilean peso' }, { code: 'COP', label: 'Colombian peso' },
    { code: 'ARS', label: 'Argentine peso' }, { code: 'ZAR', label: 'South African rand' },
    { code: 'NGN', label: 'Nigerian naira' }, { code: 'KES', label: 'Kenyan shilling' },
    { code: 'EGP', label: 'Egyptian pound' }, { code: 'AED', label: 'UAE dirham' },
    { code: 'SAR', label: 'Saudi riyal' }, { code: 'ILS', label: 'Israeli shekel' },
    { code: 'TRY', label: 'Turkish lira' }, { code: 'INR', label: 'Indian rupee' },
    { code: 'PKR', label: 'Pakistani rupee' }, { code: 'SGD', label: 'Singapore dollar' },
    { code: 'MYR', label: 'Malaysian ringgit' }, { code: 'THB', label: 'Thai baht' },
    { code: 'IDR', label: 'Indonesian rupiah' }, { code: 'PHP', label: 'Philippine peso' },
    { code: 'VND', label: 'Vietnamese dong' }, { code: 'HKD', label: 'Hong Kong dollar' },
    { code: 'JPY', label: 'Japanese yen' }, { code: 'KRW', label: 'South Korean won' },
    { code: 'CNY', label: 'Chinese yuan' }, { code: 'TWD', label: 'Taiwan dollar' }
  ];
  function curCode() {
    var v = (get(CUR_KEY, '') || '').toUpperCase();
    if (v === 'US') return 'USD';          /* pre-r197 country values migrate */
    if (v === 'CA') return 'CAD';
    return /^[A-Z]{3}$/.test(v) ? v : 'USD';
  }
  function curRow() {
    var row = el('div', { class: 'ld-settings__row' });
    row.appendChild(el('div', { class: 'ld-settings__text' }, [
      el('span', { class: 'ld-settings__lbl', text: 'Currency' }),
      el('span', { class: 'ld-settings__hint', text: 'The symbol Pricing quotes in. Changes the symbol, not the amounts.' })
    ]));
    var sel = el('select', { class: 'ld-settings__input ld-settings__select', 'aria-label': 'Currency' });
    var cur = curCode(), seen = false;
    CURRENCIES.forEach(function (c) {
      var o = el('option', { value: c.code, text: c.code + ' · ' + c.label });
      if (c.code === cur) { o.setAttribute('selected', 'selected'); seen = true; }
      sel.appendChild(o);
    });
    if (!seen) sel.value = 'USD';
    sel.addEventListener('change', function () { if (sel.value) set(CUR_KEY, sel.value); });
    row.appendChild(sel);
    return row;
  }
  /* ============================================================
     YOUR DATA (2026-08-03, Mariya): "a visitor still can't tell from any
     page whether their client files survive a cache clear or follow me from
     desktop to phone … an installed app that silently loses a season of
     bookings is worse than a website that does. A visible Export / Restore
     control would defuse it even before real sync exists."

     Cloud sync DOES exist and is live — but it announces itself in exactly
     one place (a line inside the Planner's client-file list), so a paying
     pro who never scrolls there has no way to know. Two jobs here:
       1. SAY the truth for this user, right now, in plain words.
       2. Give them a copy they own, and a way back from it.

     The three synced collections are the canonical "my data" (they are what
     learn-cloud-sync.js pushes): bookings, my-recipes, my-combos.
     ============================================================ */
  var DATA_KEYS = [
    { key: 'layerd-academy-bookings', label: 'client files', one: 'client file', list: 'bookings' },
    { key: 'layerd-academy-my-recipes', label: 'saved recipes', one: 'saved recipe', list: 'items' },
    { key: 'layerd-academy-my-combos', label: 'signature combinations', one: 'signature combination', list: 'combos' }
  ];
  function countIn(key, listName) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return 0;
      var p = JSON.parse(raw);
      var arr = p && p[listName];
      return (arr && arr.length) || 0;
    } catch (e) { return 0; }
  }
  function syncLine() {
    var cs = window.LAYERD && window.LAYERD.cloudSync;
    if (!cs || !cs.status) {
      return { tone: 'warn', text: 'Saved in this browser only. Clearing its data, or switching to another device, loses them. Export a backup to be safe.' };
    }
    var st = {};
    try { st = cs.status() || {}; } catch (e) { }
    var s = st.state || '';
    if (s === 'synced') {
      var when = '';
      try {
        if (st.lastPull) {
          var d = new Date(st.lastPull), h = d.getHours(), m = d.getMinutes();
          when = ' · last synced ' + (h % 12 || 12) + ':' + (m < 10 ? '0' : '') + m + (h < 12 ? ' am' : ' pm');
        }
      } catch (e2) { }
      return { tone: 'ok', text: 'Saved to your account ✓. They open on any device or browser you sign in on' + when + '.' };
    }
    if (s === 'migrating-up') return { tone: 'ok', text: 'Saving your files to your account right now…' };
    if (s === 'off:logged-out') return { tone: 'warn', text: 'Not backed up. You’re not logged in on this device, so your files live in this browser only. Log in and they save to your account.' };
    if (s === 'off:offline') return { tone: 'warn', text: 'Offline. Your work is safe in this browser and syncs to your account when you’re back.' };
    if (s === 'off:no-proxy') return { tone: 'warn', text: 'Can’t reach the sync server. Your files are in this browser only for now. Export a backup if you’re about to clear anything.' };
    if (s.indexOf('push-error-') === 0) return { tone: 'warn', text: 'Couldn’t save to your account (' + s.slice(11) + '). This browser still has everything. Export a backup to be safe.' };
    if (s.indexOf('off:error-') === 0) return { tone: 'warn', text: 'Sync error (' + s.slice(10) + '). Your files are in this browser only. Export a backup.' };
    return { tone: 'warn', text: 'Checking your account sync…' };
  }
  function p2d(n) { return (n < 10 ? '0' : '') + n; }
  function doExport(btn) {
    var payload = { app: 'LAYERD Toolkit', v: 1, exportedAt: new Date().toISOString(), keys: {} };
    DATA_KEYS.forEach(function (c) {
      try {
        var raw = localStorage.getItem(c.key);
        if (raw) payload.keys[c.key] = JSON.parse(raw);
      } catch (e) { /* a corrupt key must not block the rest of the backup */ }
    });
    var d = new Date();
    var name = 'layerd-toolkit-backup-' + d.getFullYear() + '-' + p2d(d.getMonth() + 1) + '-' + p2d(d.getDate()) + '.json';
    try {
      var blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      var url = URL.createObjectURL(blob);
      var a = el('a', { href: url, download: name });
      document.body.appendChild(a);
      a.click();
      setTimeout(function () { a.remove(); URL.revokeObjectURL(url); }, 1000);
      btn.textContent = 'Backup saved ✓';
      setTimeout(function () { btn.textContent = 'Export a backup'; }, 2400);
    } catch (e) {
      btn.textContent = 'Couldn’t export, try again';
    }
  }
  /* r39 (Mariya): "I want to add a download backup button for the recipe
     library too." Exposed rather than duplicated — the Recipes page calls
     this with its own two keys, so there is ONE export format, ONE naming
     convention, and Restore in Toolkit Settings already understands the
     file (it only writes keys present in the backup). */
  window.LAYERD = window.LAYERD || {};
  window.LAYERD.components = window.LAYERD.components || {};
  window.LAYERD.components.exportBackup = function (opts) {
    opts = opts || {};
    var keys = opts.keys || DATA_KEYS.map(function (c) { return c.key; });
    var payload = { app: 'LAYERD Toolkit', v: 1, exportedAt: new Date().toISOString(), keys: {} };
    keys.forEach(function (k) {
      try {
        var raw = localStorage.getItem(k);
        if (raw) payload.keys[k] = JSON.parse(raw);
      } catch (e) { /* a corrupt key must not block the rest of the backup */ }
    });
    var d = new Date();
    var name = (opts.name || 'layerd-toolkit-backup') + '-' +
      d.getFullYear() + '-' + p2d(d.getMonth() + 1) + '-' + p2d(d.getDate()) + '.json';
    try {
      var blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      var url = URL.createObjectURL(blob);
      var a = el('a', { href: url, download: name });
      document.body.appendChild(a);
      a.click();
      setTimeout(function () { a.remove(); URL.revokeObjectURL(url); }, 1000);
      return true;
    } catch (e) { return false; }
  };
  function doRestore(file, note) {
    var reader = new FileReader();
    reader.onload = function () {
      var payload;
      try { payload = JSON.parse(reader.result); } catch (e) {
        note.textContent = 'That file isn’t a LAYER’D backup. It couldn’t be read.';
        note.className = 'ld-settings__datanote is-warn';
        return;
      }
      if (!payload || !payload.keys || typeof payload.keys !== 'object') {
        note.textContent = 'That file isn’t a LAYER’D backup. No toolkit data in it.';
        note.className = 'ld-settings__datanote is-warn';
        return;
      }
      /* say what is about to happen in COUNTS, then ask — restoring replaces,
         and a season of bookings is not something to overwrite on a guess */
      var bits = [];
      DATA_KEYS.forEach(function (c) {
        var incoming = 0;
        try {
          var p = payload.keys[c.key];
          incoming = (p && p[c.list] && p[c.list].length) || 0;
        } catch (e) { }
        if (!payload.keys[c.key]) {
          bits.push('· ' + c.label + ': not in this backup, left as they are');
          return;
        }
        bits.push('· ' + c.label + ': ' + countIn(c.key, c.list) + ' now → ' + incoming + ' from the backup');
      });
      var ok = window.confirm('Restore this backup?\n\n' + bits.join('\n') +
        '\n\nThis REPLACES what is on this device. A copy of what you have now will download first.');
      if (!ok) { note.textContent = 'Restore cancelled. Nothing changed.'; note.className = 'ld-settings__datanote'; return; }
      /* safety net: never overwrite without handing back what was there */
      try { doExport({ textContent: '' }); } catch (e) { }
      var wrote = 0;
      DATA_KEYS.forEach(function (c) {
        if (!payload.keys[c.key]) return;
        try { localStorage.setItem(c.key, JSON.stringify(payload.keys[c.key])); wrote++; } catch (e) { }
      });
      note.textContent = wrote
        ? 'Restored ✓. Reloading so every tool picks it up…'
        : 'Nothing was restored. The backup held no toolkit data.';
      note.className = 'ld-settings__datanote' + (wrote ? ' is-ok' : ' is-warn');
      if (wrote) setTimeout(function () { location.reload(); }, 1200);
    };
    reader.readAsText(file);
  }
  /* ============================================================
     YOUR STUDIO (2026-08-04, Mariya): "a user pulling quotes for their own
     clients should be able to customize it with their own business logo."

     That reframed it — this is not LAYER'D's logo on a quote, it is EACH
     BAKER'S. So it cannot be a theme setting (one logo for everyone); it is
     a per-user preference stored beside their units and currency, and the
     printed quote reads it. No logo set = a clean typographic letterhead
     built from the business name, which is the "nicely designed for generic
     use" fallback she asked for.

     The image is downscaled to 600px on the way in: a 5 MB phone photo in
     localStorage would blow the quota and break saving everywhere else.
     NOTE (told her): these are DEVICE-local — cloud sync only carries
     bookings/recipes/combos, so a logo does not follow them to a phone. */
  var STUDIO_KEYS = { name: 'layerd-studio-name', contact: 'layerd-studio-contact', logo: 'layerd-studio-logo', color: 'layerd-studio-color' };
  var LOGO_MAX_PX = 600;
  function studioGet(k) { try { return localStorage.getItem(STUDIO_KEYS[k]) || ''; } catch (e) { return ''; } }
  function studioSet(k, v) {
    try {
      if (v) localStorage.setItem(STUDIO_KEYS[k], v);
      else localStorage.removeItem(STUDIO_KEYS[k]);
    } catch (e) { return false; }
    return true;
  }
  function downscaleLogo(file, cb) {
    var reader = new FileReader();
    reader.onload = function () {
      var img = new Image();
      img.onload = function () {
        var w = img.width, h = img.height;
        if (w > LOGO_MAX_PX) { h = Math.round(h * (LOGO_MAX_PX / w)); w = LOGO_MAX_PX; }
        try {
          var c = document.createElement('canvas');
          c.width = w; c.height = h;
          c.getContext('2d').drawImage(img, 0, 0, w, h);
          /* PNG keeps transparency, which most cake logos rely on */
          cb(c.toDataURL('image/png'));
        } catch (e) { cb(null); }
      };
      img.onerror = function () { cb(null); };
      img.src = reader.result;
    };
    reader.onerror = function () { cb(null); };
    reader.readAsDataURL(file);
  }
  function textRow(label, hint, key) {
    var row = el('div', { class: 'ld-settings__row' });
    row.appendChild(el('div', { class: 'ld-settings__text' }, [
      el('span', { class: 'ld-settings__lbl', text: label }),
      el('span', { class: 'ld-settings__hint', text: hint })
    ]));
    var inp = el('input', { type: 'text', class: 'ld-settings__input', value: studioGet(key), maxlength: '80' });
    inp.addEventListener('change', function () { studioSet(key, inp.value.trim()); });
    row.appendChild(inp);
    return row;
  }
  function studioRows(card) {
    card.appendChild(el('div', { class: 'ld-settings__grouphead', text: 'Your studio, printed on your quotes' }));
    card.appendChild(textRow('Business name', 'Heads every quote you print or save as a PDF.', 'name'));
    card.appendChild(textRow('Contact line', 'Website, email or phone. Whatever a client should reply to.', 'contact'));

    var row = el('div', { class: 'ld-settings__row ld-settings__row--logo' });
    row.appendChild(el('div', { class: 'ld-settings__text' }, [
      el('span', { class: 'ld-settings__lbl', text: 'Logo' }),
      el('span', { class: 'ld-settings__hint', text: 'Optional. Without one, your business name is set as the letterhead.' })
    ]));
    var prev = el('span', { class: 'ld-settings__logoprev' });
    var note = el('span', { class: 'ld-settings__logonote' });
    function paint() {
      /* this IIFE sits OUTSIDE the module closure — the shared clearNode()
         is not in scope here, same trap the el() helper hit on first render */
      while (prev.firstChild) prev.removeChild(prev.firstChild);
      var cur = studioGet('logo');
      if (cur) {
        prev.appendChild(el('img', { src: cur, alt: 'Your logo' }));
      } else {
        prev.appendChild(el('span', { class: 'ld-settings__logoempty', text: 'no logo' }));
      }
    }
    var pick = el('input', { type: 'file', accept: 'image/*', class: 'ld-settings__file' });
    var up = el('button', { class: 'ld-settings__databtn ld-settings__databtn--ghost', type: 'button', text: 'Upload' });
    var rm = el('button', { class: 'ld-settings__logorm', type: 'button', text: 'remove' });
    up.addEventListener('click', function () { pick.click(); });
    rm.addEventListener('click', function () { studioSet('logo', ''); paint(); note.textContent = ''; });
    pick.addEventListener('change', function () {
      var f = pick.files && pick.files[0];
      pick.value = '';
      if (!f) return;
      note.textContent = 'Resizing…';
      downscaleLogo(f, function (dataUrl) {
        if (!dataUrl) { note.textContent = 'That image couldn’t be read. Try a PNG or JPG.'; return; }
        if (!studioSet('logo', dataUrl)) {
          note.textContent = 'Too large to store on this device. Try a smaller image.';
          return;
        }
        paint();
        note.textContent = 'Saved ✓. It heads your printed quotes.';
      });
    });
    paint();
    row.appendChild(el('span', { class: 'ld-settings__logobox' }, [prev, up, rm, pick, note]));
    card.appendChild(row);

    /* r222 (Mariya: "a colour option in settings for a brand colour to use as
       their invoice colour"). Optional accent for the printed quote/invoice:
       the studio name, the total and the divider rules pick it up. Unset
       leaves the calm default. Only a valid hex is stored. */
    var crow = el('div', { class: 'ld-settings__row ld-settings__row--color' });
    crow.appendChild(el('div', { class: 'ld-settings__text' }, [
      el('span', { class: 'ld-settings__lbl', text: 'Brand colour' }),
      el('span', { class: 'ld-settings__hint', text: 'Optional. An accent on your printed quotes: the studio name, the total and the divider lines.' })
    ]));
    var curColor = studioGet('color');
    var isHex = /^#[0-9a-fA-F]{6}$/.test(curColor);
    var swatch = el('input', { type: 'color', class: 'ld-settings__color', value: isHex ? curColor : '#2A2138', 'aria-label': 'Brand colour' });
    var cnote = el('span', { class: 'ld-settings__colornote', text: isHex ? curColor.toUpperCase() : 'no colour set' });
    var creset = el('button', { class: 'ld-settings__logorm', type: 'button', text: 'reset' });
    swatch.addEventListener('input', function () {
      studioSet('color', swatch.value);
      cnote.textContent = swatch.value.toUpperCase();
    });
    creset.addEventListener('click', function () {
      studioSet('color', '');
      swatch.value = '#2A2138';
      cnote.textContent = 'no colour set';
    });
    crow.appendChild(el('span', { class: 'ld-settings__colorbox' }, [swatch, creset, cnote]));
    card.appendChild(crow);
  }

  function dataRow() {
    var row = el('div', { class: 'ld-settings__row ld-settings__row--data' });
    var head = el('div', { class: 'ld-settings__text' }, [
      el('span', { class: 'ld-settings__lbl', text: 'Your data' }),
      el('span', { class: 'ld-settings__hint', text: 'Where your client files, recipes and combinations live.' })
    ]);
    row.appendChild(head);

    /* 2026-08-21 (her ask, the two-accounts confusion): say WHOSE account
       this page is signed into, right where the data is explained. The
       email comes from the subnav's Liquid stamp - absent (logged out or
       an old paste) says so honestly instead of guessing. */
    var idNav = document.querySelector('.learn-subnav[data-lh-cid]');
    var signedEmail = idNav && idNav.getAttribute('data-lh-email');
    row.appendChild(el('p', { class: 'ld-settings__counts', text: signedEmail
      ? 'Signed in as ' + signedEmail + '.'
      : 'Not signed in on this browser.' }));

    var st = syncLine();
    var status = el('p', { class: 'ld-settings__sync is-' + st.tone, text: st.text });
    row.appendChild(status);

    var counts = DATA_KEYS.map(function (c) {
      var n = countIn(c.key, c.list);
      return n + ' ' + (n === 1 ? c.one : c.label);
    }).join(' · ');
    row.appendChild(el('p', { class: 'ld-settings__counts', text: 'You have ' + counts + '.' }));

    var note = el('p', { class: 'ld-settings__datanote' });
    var exp = el('button', { class: 'ld-settings__databtn', type: 'button', text: 'Export a backup' });
    exp.addEventListener('click', function () { doExport(exp); });
    var pick = el('input', { type: 'file', accept: 'application/json,.json', class: 'ld-settings__file' });
    pick.addEventListener('change', function () {
      if (pick.files && pick.files[0]) doRestore(pick.files[0], note);
      pick.value = '';
    });
    var res = el('button', { class: 'ld-settings__databtn ld-settings__databtn--ghost', type: 'button', text: 'Restore from a backup' });
    res.addEventListener('click', function () { pick.click(); });

    /* 2026-09-17 (Mariya: a file made on one device wasn't showing on the
       other). Sync runs in the background, so when it stalls there is no way
       to see why or to force it. This button pulls the account's copy in and
       pushes this device's up on demand, then shows the outcome on the status
       line above — a manual lever + a visible answer. */
    var syncBtn = el('button', { class: 'ld-settings__databtn ld-settings__databtn--ghost', type: 'button', text: 'Sync now' });
    syncBtn.addEventListener('click', function () {
      var cs = window.LAYERD && window.LAYERD.cloudSync;
      if (!cs || !cs.syncNow) {
        note.textContent = 'Account sync isn’t available on this page.';
        note.className = 'ld-settings__datanote is-warn';
        return;
      }
      syncBtn.disabled = true;
      var label = syncBtn.textContent;
      syncBtn.textContent = 'Syncing…';
      note.textContent = '';
      note.className = 'ld-settings__datanote';
      function settle() {
        var s2 = refreshStatus();
        syncBtn.disabled = false;
        syncBtn.textContent = s2.tone === 'ok' ? 'Synced ✓' : 'Sync now';
        setTimeout(function () { syncBtn.textContent = label; }, 2600);
      }
      var p;
      try { p = cs.syncNow(); } catch (e) { p = null; }
      /* the push side finishes a beat after the pull resolves; give it room,
         then read the status either way (promise or not) */
      if (p && p.then) { p.then(function () { setTimeout(settle, 900); }, function () { setTimeout(settle, 900); }); }
      else { setTimeout(settle, 1400); }
    });

    row.appendChild(el('div', { class: 'ld-settings__databtns' }, [exp, res, syncBtn, pick]));
    /* r85 (Mariya's wipe scare): the ONE warning that would have prevented
       it, stated where backups live. Deleting the installed app deletes
       that app's copy of everything on this panel — iOS gives web apps no
       way to intercept or warn at delete time, so the warning lives here,
       permanently, next to the button that makes it survivable. */
    row.appendChild(el('p', { class: 'ld-settings__datanote ld-settings__datawarn',
      text: '⚠ Deleting the app from your home screen deletes this device’s copy. Export a backup first. Pro accounts also keep a synced copy.' }));
    row.appendChild(note);
    /* the status is live — the sync layer changes it after login/push */
    function refreshStatus() {
      var s2 = syncLine();
      status.textContent = s2.text;
      status.className = 'ld-settings__sync is-' + s2.tone;
      return s2;
    }
    try {
      window.addEventListener('layerd:cakefile', refreshStatus);
    } catch (e) { }
    /* Her live find 2026-08-13: the event above is the ONLY repaint, and a
       pull that finds both sides already identical — the normal steady
       state — emits no event at all (learn-cloud-sync announce()s only on
       anyChanged). So the line rendered once before the pull resolved,
       while the state was still 'init', and sat on "Checking your account
       sync…" forever — on the very device whose sync was perfectly
       healthy. Poll until the state resolves, then stop; the event
       listener carries every later change. */
    try {
      var tries = 0;
      var iv = setInterval(function () {
        var s2 = refreshStatus();
        var settled = s2.text.indexOf('Checking') !== 0;
        if (settled || ++tries >= 30) clearInterval(iv);
      }, 1000);
    } catch (e2) { }
    return row;
  }

  function build(mount) {
    while (mount.firstChild) mount.removeChild(mount.firstChild);
    /* r99 (Mariya, 2026-08-25): on the Toolkit landing page the settings
       panel is a long card the tools have to be scrolled past — it FOLDS
       now, closed by default. The gear's overlay (data-ld-settings-modal)
       is a deliberate visit, so it still opens straight onto the controls. */
    var collapsible = !mount.hasAttribute('data-ld-settings-modal');
    var card = el(collapsible ? 'details' : 'div', { class: 'ld-settings' + (collapsible ? ' ld-settings--fold' : '') });
    if (collapsible) {
      card.appendChild(el('summary', { class: 'ld-settings__head ld-settings__sum' }, [
        el('span', { class: 'ld-settings__eyebrow', text: 'Toolkit settings' }),
        el('span', { class: 'ld-settings__sub', text: 'Measurements, currency, mixer bowl, your studio details and your data.' }),
        el('span', { class: 'ld-settings__chev', 'aria-hidden': 'true', text: '▾' })
      ]));
      /* a #settings deep link (a stale bundle's gear, a bookmark) still
         lands on an OPEN panel — the link promised the controls */
      try { if ((location.hash || '') === '#settings') card.open = true; } catch (eH) { }
    } else {
      card.appendChild(el('div', { class: 'ld-settings__head' }, [
        el('span', { class: 'ld-settings__eyebrow', text: 'Toolkit settings' }),
        el('span', { class: 'ld-settings__sub', text: 'Set these once. Every tool follows. You can still flip them on any page.' })
      ]));
    }
    card.appendChild(seg('Measurements', 'Tier sizes and pan sizes across the suite.',
      UNITS_KEY, [{ v: 'in', t: 'inches' }, { v: 'cm', t: 'cm' }], 'in'));
    card.appendChild(curRow());
    /* r86 (2026-08-23): the r62 rename is REVERTED — she re-checked the
       box and the mixer is 6 qt after all. Same numbers throughout, label
       restored; a '7' stored during the two-day r62 window migrates back. */
    if (get(BOWL_KEY, '') === '7') set(BOWL_KEY, '6');
    card.appendChild(seg('Mixer bowl', 'How Batch Math checks a batch will fit.',
      BOWL_KEY, [{ v: '5', t: '5 qt' }, { v: '6', t: '6 qt' }], '5'));
    studioRows(card);
    card.appendChild(dataRow());
    /* 2026-08-04: the way in to the report page. Quiet — findable, not
       advertised. 2026-09-14 (Mariya): moved to the very bottom, BELOW Account
       and About the Toolkit. In the Studio those links come after this card, so
       the report link now mounts into [data-ld-report] (rendered last by the
       spine) rather than here. The href stays as a no-JS fallback path. */
    var reportHost = document.querySelector('[data-ld-report]');
    if (reportHost && !reportHost.querySelector('.ld-settings__report')) {
      reportHost.appendChild(el('a', {
        class: 'ld-settings__report', href: '/pages/report-a-problem',
        text: 'Something not working? Report a problem →'
      }));
    }
    mount.appendChild(card);
  }
  function mountAll() {
    var hosts = document.querySelectorAll('[data-ld-settings]');
    Array.prototype.forEach.call(hosts, build);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountAll);
  else mountAll();
  /* exported so a panel added after load (or a page that builds its own
     chrome) can mount it without waiting for a reload */
  window.LAYERD = window.LAYERD || {};
  window.LAYERD.components = window.LAYERD.components || {};
  window.LAYERD.components.mountSettings = mountAll;
})();

/* ============================================================
   r24 (2026-07-31) — the settings door, on every tool page.
   Mariya: "maybe just a small icon top right throughout the full
   suite." Injected rather than added to six templates: each tool
   grew its own header class, so one function that finds whichever
   header is present keeps them identical and keeps the edit in
   one file. It is a quiet link, not a nag - the tools are still
   the reason to be on the page.
   ============================================================ */
(function initLdSettingsGear() {
  /* 2026-09-14 (Mariya: "there is a completely different set of settings when
     you click the settings button on the native pages ... we need to eliminate
     all the settings from the native pages"). Settings live in ONE place now —
     the Studio's Settings view, reached from the bottom bar's Settings tab. The
     per-page gear that opened a SEPARATE in-page settings panel (r24 / r81) is
     removed, and the in-context "change in settings" doors (.ld-setopen: the
     units chip, Batch Math's bowl note, the Planner reminders door) DEEP-LINK to
     the Studio instead of opening a modal. One settings surface, nothing to
     drift. The old overlay (openSettingsModal / .ld-setmodal) is gone; the
     .ld-setgear / .ld-setmodal CSS is now unused. */
  var SETTINGS_URL = '/pages/studio?ldsp=account';
  document.addEventListener('click', function (ev) {
    var a = ev.target && ev.target.closest ? ev.target.closest('a.ld-setopen') : null;
    if (!a) return;
    ev.preventDefault();
    try { location.href = SETTINGS_URL; } catch (e) { }
  });
})();

/* ============================================================
   r92 (Mariya, 2026-08-23) — the units control becomes a
   SETTINGS CHIP, suite-wide: "under batch math … it is a
   different pill button for mixer bowl size … when you click
   it, the settings popup comes up. can we do that same format
   for the rest of the pages for the inch/cm buttons?"
   The page's own inches/cm pill stays in the DOM but hides —
   its click handlers are each tool's re-render fan-out, so when
   the settings popup flips the units this code clicks the
   hidden pill's matching button and the page relabels itself
   with zero per-tool changes. The chip reads "inches · change
   in settings" and opens the same overlay the gear does
   (a.ld-setopen). Batch Math is skipped here — it builds its
   own COMBINED units + bowl chip. (This replaces r91's labelled
   pill groups, which never shipped.)
   ============================================================ */
(function initLdUnitChip() {
  var UNITS_KEY = 'layerd-learn-units';
  function pref() {
    try { return localStorage.getItem(UNITS_KEY) === 'cm' ? 'cm' : 'in'; } catch (e) { return 'in'; }
  }
  function label() { return (pref() === 'cm' ? 'cm' : 'inches') + ' · change in settings'; }
  var chipEl = null, lastPref = null;
  function syncFromSettings() {
    if (chipEl && chipEl.isConnected) chipEl.textContent = label();
    var want = pref();
    if (want === lastPref) return;
    lastPref = want;
    /* re-find every time — a tool may rebuild its pill on render */
    var pill = document.querySelector('.ld-unitchip-hidden');
    if (!pill) return;
    var btns = pill.querySelectorAll('button');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      var t = ((b.getAttribute('data-unit') || b.textContent) || '').trim().toLowerCase();
      if ((want === 'cm' && t === 'cm') || (want === 'in' && (t === 'in' || t === 'inches'))) {
        b.click();   /* the page's own listeners do the relabelling */
        break;
      }
    }
  }
  function place() {
    if (document.querySelector('.ld-unitchip')) return true;
    if (document.querySelector('.bmath-units')) return true;   /* batch math: its own combined chip */
    var pill = document.querySelector('.lrn-units, .ldc-units');
    if (!pill || !pill.parentNode) return false;
    if (pill.closest('.ld-settings__row')) return false;       /* never the settings panel's own segment */
    pill.classList.add('ld-unitchip-hidden');
    var chip = document.createElement('a');
    chip.className = 'ld-unitchip ld-setopen';
    chip.href = '/pages/studio?ldsp=account';   /* 2026-09-14: settings live in the Studio now */
    chip.title = 'Measurement units, change in Toolkit settings';
    chip.textContent = label();
    /* r99 (Mariya, 2026-08-25): "the inches button should be left aligned
       and have a bit of white space under it". The chip is inline-flex, so
       inside the theme's centred column it floated to the middle and sat
       flush on the card below. It gets its own row now — the SAME wrap the
       hidden pill carried, so it lines up with the cards, is left-aligned
       whatever the parent's text-align, and keeps a margin underneath. */
    /* deliberately NOT .learn-wrap: the row sits in the same box as the
       cards below it, so inheriting the parent's edge lines the chip up
       with them (the wrap's own 32px padding pushed it inboard, and its
       auto side-margins were what re-centred it) */
    var row = document.createElement('div');
    row.className = 'ld-unitchip-row';
    row.appendChild(chip);
    pill.parentNode.insertBefore(row, pill);
    chipEl = chip;
    lastPref = pref();
    return true;
  }
  window.addEventListener('storage', function (e) {
    if (!e || !e.key || e.key === UNITS_KEY) setTimeout(syncFromSettings, 0);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', place);
  else place();
  [400, 1200, 2500].forEach(function (ms) { setTimeout(place, ms); });
})();

/* ============================================================
   r96 (Mariya's "1b Plum band" mock, 2026-08-24) — the suite
   header becomes the plum band, on every tool page:
     quiet bar:  ← Toolkit ················· ⚙
     ink band:   LAYER'D TOOLKIT ······ <tier tag, gold caps>
                 <serif title, ivory>
                 — gold dash
   and the client chip card below lifts INTO the band (CSS
   overlap). One builder restructures whichever page header is
   present — the pages' own markup, classes and the gear keep
   their identities and listeners; they are only re-seated.
   Supersedes the r95 back-pill layout.
   ============================================================ */
(function initLdHeadBand() {
  var HEADS = ['.ld-head--compact', '.bmath-head', '.ldp-head', '.ldc-head', '.lstr-head', '.cker-head--compact'];
  function adoptGear(head) {
    var bar = head.querySelector('.ld-headband__bar');
    var gear = head.querySelector('.ld-setgear');
    if (bar && gear && gear.parentNode !== bar) bar.appendChild(gear);
  }
  function build() {
    var head = null;
    for (var i = 0; i < HEADS.length && !head; i++) head = document.querySelector(HEADS[i]);
    if (!head) return false;
    if (head.classList.contains('ld-headband')) { adoptGear(head); return true; }
    var title = head.querySelector('h1');
    if (!title) return false;
    var back = head.querySelector('a[class*="__back"]');
    var tag = head.querySelector('[class*="__tag"]');
    var lede = head.querySelector('[class*="__lede"]');

    head.classList.add('ld-headband');
    var bar = document.createElement('div');
    bar.className = 'ld-headband__bar';
    if (back) bar.appendChild(back);

    var eyerow = document.createElement('div');
    eyerow.className = 'ld-headband__eyerow';
    var eyebrow = document.createElement('span');
    eyebrow.className = 'ld-headband__eyebrow';
    eyebrow.textContent = 'LAYER’D Toolkit';
    eyerow.appendChild(eyebrow);
    if (tag) eyerow.appendChild(tag);
    /* 2026-09-18 (Mariya): an Academy member's "Academy →" switch rides the
       eyerow too (right of the eyebrow), so Cake ER reads "LAYER’D Toolkit
       ······ Academy →" like the Studio instead of leaving the pill to wrap
       below the title. It keeps its own class (not __tag), so the eyerow's
       plain-caps strip styling never flattens the pill. */
    var xswitch = head.querySelector('[class*="head__academy"]');
    if (xswitch) eyerow.appendChild(xswitch);

    var band = document.createElement('div');
    band.className = 'ld-headband__band';
    band.appendChild(eyerow);
    band.appendChild(title);
    var dash = document.createElement('span');
    dash.className = 'ld-headband__dash';
    dash.setAttribute('aria-hidden', 'true');
    band.appendChild(dash);
    if (lede) band.appendChild(lede);

    head.insertBefore(band, head.firstChild);
    head.insertBefore(bar, band);
    adoptGear(head);
    return true;
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', build);
  else build();
  [400, 1200, 2500].forEach(function (ms) { setTimeout(build, ms); });
})();

/* ===================== identity card + Next (desktop, r226) =============
   Mariya (2026-09-04): roll the pricing design-system view - a plum identity
   card (the page title + the client chip) and a Next button pointing at the
   next pipeline step - to the other pipeline pages (Serving, Structure, Batch
   Math, the Planner). DESKTOP ONLY (>= 900px): below that the page keeps its
   phone view unchanged - the hero band shows and the chip + inches stay where
   they are. Pricing has its own rail (.ldc-pr), so this skips it, and pages
   with no client chip (Cake ER, Recipes, Pantry, Blooms) never build it. */
(function identityCard() {
  var PAGES = {
    serving: { slug: 'serving-size-calculator', label: 'Serving' },
    pricing: { slug: 'pricing', label: 'Pricing' },
    structure: { slug: 'structure', label: 'Structure' },
    batch: { slug: 'batch-math', label: 'Batch Math' },
    planner: { slug: 'planner', label: 'Planner' }
  };
  function el(t, c, h) { var e = document.createElement(t); if (c) e.className = c; if (h != null) e.innerHTML = h; return e; }

  function resolveNext(curKey) {
    try {
      var P = window.LdCakeFile && window.LdCakeFile.PIPELINE;
      if (!P || !curKey) return null;
      var order = P.map(function (p) { return p.key; });
      var idx = order.indexOf(curKey);
      if (idx < 0) return null;
      var tabs = [].slice.call(document.querySelectorAll('.learn-subnav__tab'));
      function hrefFor(key) {
        var pg = PAGES[key];
        if (!pg) return null;
        var href = '/pages/' + pg.slug, label = pg.label;
        for (var i = 0; i < tabs.length; i++) {           /* prefer the live subnav href */
          var t = tabs[i], th = t.getAttribute('href') || '';
          if ((t.textContent || '').trim().toLowerCase() === label.toLowerCase() && th && th !== '#') { href = th; break; }
        }
        var b = window.LdCakeFile.resolveScope && window.LdCakeFile.resolveScope();
        if (b && b.id) href += (href.indexOf('?') >= 0 ? '&' : '?') + 'booking=' + encodeURIComponent(b.id);
        return { href: href, label: label };
      }
      for (var i = idx + 1; i < order.length; i++) { var f = hrefFor(order[i]); if (f) return f; }
    } catch (e) { }
    return null;
  }

  function build() {
    if (document.querySelector('.ldc-pr')) return true;          /* pricing owns its own rail */
    if (document.querySelector('.ldc-idcard')) return true;      /* already built */
    /* 2026-09-17 (Mariya): every tool page that carries the shared nav sidebar
       (.lh-tabbar) drops the identity card. The Studio's own layout is nav +
       content with NO id-card, so this is what "match the Studio" means; the
       id-card also (a) hid the headband's "← Toolkit" back link and replaced it
       with a "Next: →" she wants gone, and (b) added a third column that pushed
       the tool content off-screen (Structure's diagram was cut off). Dropping it
       restores the back link, removes Next, and gives the content the full width.
       Pricing keeps its own rail (.ldc-pr guard above); the Planner never built
       an id-card. */
    if (document.querySelector('.lh-tabbar')) return true;
    var head = document.querySelector('.ld-headband');
    var chipRow = document.querySelector('.ld-cakechip-row');
    if (!head || !chipRow || !head.parentElement) return false;  /* wait for the header + chip */
    /* Pattern B pages (Structure, Batch Math, the Planner) wrap the header in a
       full-bleed .learn-page and render the tool into a separate
       `main.learn-wrap`; the rail belongs in that main. If the header sits in a
       .learn-page but the main has not mounted yet, wait for it - otherwise the
       rail lands in the full-bleed wrapper and indents the subnav. */
    if (head.closest('.learn-page') && !document.querySelector('main.learn-wrap')) return false;

    var titleEl = head.querySelector('.ldc-head__title, .ld-headband__title, [class*="head__title"]');
    var title = titleEl ? titleEl.textContent.trim() : '';
    var eyebrowEl = head.querySelector('.ld-headband__eyebrow');
    var eyebrow = eyebrowEl ? eyebrowEl.textContent.trim() : 'LAYER’D Toolkit';
    var tagEl = head.querySelector('.ldc-head__tag, .ld-headband__tag, [class*="head__tag"]');
    var tag = tagEl ? tagEl.textContent.trim() : '';
    var toolMount = document.querySelector('[data-ld-cakechip][data-ld-tool]');
    var curKey = toolMount ? toolMount.getAttribute('data-ld-tool') : '';
    if (curKey === 'timeline') curKey = 'planner';
    if (curKey === 'batch-math') curKey = 'batch';   /* the mount's key, the pipeline's key */
    var next = resolveNext(curKey);

    /* the rail card: eyebrow + title + rule + chip + inches + Next, stacked */
    var card = el('div', 'ldc-idcard');
    card.appendChild(el('div', 'ldc-idcard__kick', '<span>' + eyebrow + '</span>' + (tag ? '<span class="ldc-idcard__tag">' + tag + '</span>' : '')));
    if (title) card.appendChild(el('div', 'ldc-idcard__title', title));
    card.appendChild(el('div', 'ldc-idcard__bar'));
    var chipSlot = el('div', 'ldc-idcard__clientslot'); card.appendChild(chipSlot);
    var unitSlot = el('div', 'ldc-idcard__unitslot'); card.appendChild(unitSlot);
    if (next) { var a = el('a', 'ldc-idcard__next', 'Next: ' + next.label + ' →'); a.href = next.href; card.appendChild(a); }

    /* the content container hosts the rail. Pages built on `main.learn-wrap`
       (Structure, Batch Math, the Planner) keep the subnav, header and content
       as separate siblings of a full-bleed page wrapper, so the rail belongs
       INSIDE the main content - not the wrapper (which also holds the subnav,
       which must stay full width). Serving's content is the headband's own
       wrap, so fall back to that. */
    var host = document.querySelector('main.learn-wrap') || head.parentElement;
    host.insertBefore(card, (head.parentNode === host) ? head : host.firstChild);
    host.classList.add('ldc-idcard-host');
    document.documentElement.classList.add('ldc-idcard-on');

    /* remember the chip + inches homes so mobile can put them back as the
       headband's siblings (the suite attaches the chip with an adjacent-sibling
       rule; on the phone it must sit right after the hero band). */
    var unitRow = document.querySelector('.ld-unitchip-row');
    var chipHome = { p: chipRow.parentNode, n: chipRow.nextSibling };
    var unitHome = unitRow ? { p: unitRow.parentNode, n: unitRow.nextSibling } : null;
    var mq = window.matchMedia ? window.matchMedia('(min-width: 900px)') : { matches: true };
    function place() {
      var cr = document.querySelector('.ld-cakechip-row');
      var ur = document.querySelector('.ld-unitchip-row');
      if (mq.matches) {                                    /* desktop: into the rail card */
        if (cr && cr.parentNode !== chipSlot) { cr.classList.add('ldc-idcard__clientmount'); chipSlot.appendChild(cr); }
        if (ur && ur.parentNode !== unitSlot) { ur.classList.add('ldc-idcard__unitrow'); unitSlot.appendChild(ur); }
      } else {                                             /* mobile: back where they were */
        if (cr && cr.parentNode === chipSlot && chipHome.p) chipHome.p.insertBefore(cr, chipHome.n);
        if (ur && unitHome && ur.parentNode === unitSlot && unitHome.p) unitHome.p.insertBefore(ur, unitHome.n);
      }
    }
    place();
    try { if (mq.addEventListener) mq.addEventListener('change', place); else if (mq.addListener) mq.addListener(place); } catch (e) { }
    /* r228: bounded retries catch the chip/inches async mount - NOT a persistent
       document.body observer (that anti-pattern was the pricing flashing loop;
       keep it out of the shared code). */
    [300, 900, 1800].forEach(function (ms) { setTimeout(place, ms); });
    return true;
  }

  function boot() { if (!build()) [300, 800, 1600, 2800].forEach(function (ms) { setTimeout(build, ms); }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();

/* ============================================================
   SPINE r236: return-to-studio. When a tool is opened from the workspace
   (studio) it carries ?return=<same-site path>. Rewrite the tool's own back
   link to that path, labelled "Studio", so the baker comes home instead of
   landing on the marketing hub. Same-site paths only; no-op otherwise.
   ============================================================ */
(function () {
  /* 2026-09-18 (Mariya): never rewrite an Academy-only tool's back link to the
     Toolkit - Bloom Atlas returns to the Academy, not the studio. */
  if (document.querySelector('.ld-academy-tool')) return;
  var m = /[?&]return=([^&]+)/.exec(location.search);
  if (!m) return;
  var url; try { url = decodeURIComponent(m[1]); } catch (e) { url = m[1]; }
  if (!/^\/[A-Za-z0-9\-\/_.?=&%]*$/.test(url)) return;   /* same-site path only */
  var SELS = '.ldp-head__back, .bmath-head__back, .lstr-head__back, .cker-head__back, .ldc-head__back, .ld-head--compact .ld-head__back';   /* UX 2026-09-07: + Serving (.ldc-head__back) */
  function apply() {
    var hit = false;
    var nodes = document.querySelectorAll(SELS);
    for (var i = 0; i < nodes.length; i++) {
      var a = nodes[i];
      a.setAttribute('href', url);
      a.textContent = '← Toolkit';   /* UX 2026-09-07: matches the rail label for /pages/studio */
      hit = true;
    }
    return hit;
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { if (!apply()) [300, 900, 1800].forEach(function (t) { setTimeout(apply, t); }); });
  else if (!apply()) [300, 900, 1800].forEach(function (t) { setTimeout(apply, t); });
})();
